// Milestone 2 — evolves the Milestone 1 AI CGO Home into the approved
// 6-region hierarchy:
//   1. What Martian checked     — real prompts, freshness, "how Martian checks"
//   2. What Martian found       — mention evidence, competitors, sources
//   3. What needs attention   — up to 3 real findings
//   4. Martian recommends       — one dominant action, "why?" shows real evidence
//   5. What Martian is learning — progressive change/trend language, gated
//   6. Agents at work         — real activity, secondary
// "Martian checked -> Martian found -> Martian understands -> Martian recommends
// -> Martian is learning." Region 4 stays the primary outcome; region 6
// stays secondary, per the locked hierarchy.
//
// Every new data point reuses existing, already-real endpoints -- none
// of this is a new capability, only a new place real evidence is shown:
//   GET /brand/briefing            (existing, now also returns client.id)
//   GET /brand/briefing/status     (Milestone 1 polling, unchanged)
//   GET /agents/activity/recent    (Milestone 1, unchanged)
//   GET /v2/intelligence/{id}/sources      (real, already used by the paid
//                                            AIDiscoveryCommandCenterPage)
//   GET /v2/intelligence/{id}/changes      (real ChangeDetector output --
//                                            empty until Milestone 2 Phase 0's
//                                            data-sufficiency gates are met)
//   GET /v2/som-prompts/{id}       (real SomPromptManager prompts)
//   overview.competitors from the existing GET /brand/briefing response
//   (VisibilityService.client_overview) -- no new call needed for "who's
//   appearing instead."
//
// Honesty discipline (Milestone 2 language rules): no per-AI-surface
// claims (no "ChatGPT visibility" etc.), no trend/change language until
// the real gates in services.py are satisfied, no fabricated sentiment,
// no vanity score. The one technical-methodology note lives behind a
// quiet "How Martian checks" toggle, never as a prominent leading
// disclaimer.
//
// Milestone 3 -- evolves region 4 in place, "Martian recommends" ->
// "Martian prepared this for you": once the real PreparedWorkService
// (prepared_work.py) has produced a structured brief for the same
// dominant finding, this section renders that real, persisted work item
// instead of just the finding text -- with a human-only status workflow
// (Prepared -> Reviewing -> Approved/Dismissed). "Approved" only means
// the team accepted the work; Martian never executes or auto-transitions
// it. Until real prepared work exists, this section falls back to the
// exact Milestone 2 rendering below.
//   GET /prepared-work/{clientId}                       (real, new)
//   POST /prepared-work/{clientId}/{workId}/status        (real, new --
//                                                    the ONLY status writer)
//
// Milestone 6 (AI CGO Experience Integration) -- this page is now Martian
// Home for every brand tenant at every plan (App.js's HomeRouter), not
// just a free-plan teaser. Two changes: (1) the "Activate full
// workspace" CTA only makes sense pre-activation, so it's gated behind
// user.plan === 'free' (useAuth()) rather than shown unconditionally;
// (2) approved/dismissed prepared-work items now show the real decision
// date (prepared_work's own `reviewed_at`) plus, for an approved item,
// an explicit statement that Martian has not observed or recorded an
// outcome for it -- audited directly against prepared_work.py's
// update_status(): it persists only status + reviewed_at, nothing else,
// so that is the entire honest truth available today. No cadence, check
// date, or business outcome is invented.
//
// Milestone 7A (Unified Intelligence Workspace) -- restructures the
// centre workspace by MEANING, not by data source, into four distinct
// states: Needs Your Decision (a real open prepared-work item, shown
// only when one genuinely exists) / What's Happening (real
// ChangeDetector output, or an honest baseline) / What Martian Found (the
// real ranked findings, with the underlying checked-evidence collapsed
// behind one disclosure instead of five permanently-open sections) /
// Prepared For You (the complete, real prepared_work queue, not just a
// "+N other things" count). No new data source, no new scoring model --
// every real call this page already made is kept exactly as-is; only
// the arrangement changes. rank_prepared_work()'s existing status-tier
// -> confidence -> recency ordering (prepared_work.py) is reused as-is
// for both "which item is the one open decision" and the ordering of
// the rest -- no new priority model was invented, per the explicit
// instruction not to pretend a cross-agent importance score exists when
// it doesn't.
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@/components/DashboardLayout';
import { GLIMState, resolveProductState } from '@/brand';
import { TalkToMartianPanel, LIMITATION_KIND_LABEL } from '@/components/TalkToMartianPanel';
import { AskMartianButton } from '@/components/AskMartianButton';
import RecommendationsAwaitingCard from '../../components/brand/RecommendationsAwaitingCard';
import GrowthOSBridgeSignal from '../../components/brand/GrowthOSBridgeSignal';
import MartianLearningState from '../../components/brand/MartianLearningState';
import CompanyContextRail from '../../components/brand/CompanyContextRail';
import { briefingAPI, agentsAPI, intelligenceAPI, somPromptsAPI, preparedWorkAPI, companyContextAPI } from '@/lib/api';
import { socialFactsAPI } from '@/lib/intelligenceApi';
import {
  EVIDENCE_LABELS as DOMAIN_EVIDENCE_LABELS, EVIDENCE_SKIP_KEYS as DOMAIN_EVIDENCE_SKIP_KEYS,
  formatEvidenceValue as formatDomainEvidenceValue, isDisplayableEvidenceValue,
} from '@/lib/evidenceFormatting';
import { useAuth } from '@/contexts/AuthContext';
import { ArrowRight, ListMagnifyingGlass, CaretDown, Info, TrendUp, WarningCircle } from '@phosphor-icons/react';
import { toast } from 'sonner';

// Martian Centre Workspace UX Refactor -- real, already-stored Company
// Context fields (company_context.py's TEXT_FIELDS/LIST_FIELDS), reduced
// to a compact, non-repeating chip. Never the whole profile on Martian
// Home -- the brand controls the full picture at its own /company-context
// route; this is only a small "Martian is using this" signal, built ONLY
// from fields the brand has actually set (source is genuinely null for
// anything unset -- nothing here is invented to fill the chip).
function companyContextSummary(ctx) {
  if (!ctx) return null;
  const parts = [ctx.primary_market?.value, ctx.business_summary?.value].filter(Boolean);
  return parts.length > 0 ? parts.join(' · ') : null;
}

// Milestone 15 (Visual Identity Rebuild) -- section 12's own example
// ("WORK READY" + a rule + an open list) is explicitly NOT a bordered
// box. This one real usage (Prepared for you) was previously the
// rounded-card-with-shadow treatment that made every section look like
// a form; now a plain eyebrow + a single hairline rule, matching
// SignalRow's own open-list geometry below it.
// Milestone 17 (Experience Model Rebuild) -- the eyebrow drops from
// full-ink/12px to muted/11px: TODAY's own eyebrow ("Martian's take")
// keeps the stronger, full-ink treatment, so this section reads as
// quieter peripheral context by typography alone, never by a border or
// a box -- the exact "use whitespace and type scale, not containers,
// for hierarchy" instruction.
function Section({ eyebrow, children, testId, className = '' }) {
  return (
    <section className={className} data-testid={testId}>
      <p className="text-[11px] font-semibold uppercase tracking-[0.04em] text-[var(--martian-ink-muted,var(--text-secondary))] mb-2">{eyebrow}</p>
      <div className="border-t border-[var(--martian-rule,var(--border))] pt-3">
        {children}
      </div>
    </section>
  );
}

// Milestone 13 (Re-architect the Brand Home UX) -- one compact signal
// row: dot + origin + title + arrow, nothing else. This is the entire
// fix for "four vertically-stacked sections" -- everything that isn't
// today's one dominant NOW story renders as a single scannable line
// here instead of its own bordered card. Clicking hands the same real
// finding to Talk to Martian (askMartianAboutFinding/askMartianAboutChange) --
// investigation happens in conversation or in the relevant surface, not
// by expanding a paragraph inline.
function SignalRow({ origin, title, onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId || 'signal-row'}
      className="group w-full flex items-center gap-2.5 py-3 text-left hover:bg-[var(--martian-surface,var(--canvas-secondary))] px-1 -mx-1"
    >
      {/* A single violet dot -- "Martian noticed this" -- not a fabricated
           severity/status classification the underlying data doesn't
           carry (a real per-signal problem/positive judgment doesn't
           exist here; see the module's own anti-fabrication discipline). */}
      <span className="w-1.5 h-1.5 shrink-0 bg-[var(--martian-violet,var(--accent))]" aria-hidden="true" />
      <span className="text-[9.5px] font-mono uppercase tracking-wide text-[var(--martian-ink-muted,var(--text-tertiary))] shrink-0 w-[110px] truncate">{origin}</span>
      <span className="text-[13px] text-[var(--martian-ink,var(--text-primary))] leading-snug flex-1 min-w-0 truncate">{title}</span>
      {/* Milestone 16, section 17 -- "arrow moves slightly right" on
           hover: a deliberate, mechanical micro-interaction, never a
           bounce/gradient/glow. */}
      <ArrowRight size={12} className="text-[var(--martian-ink-muted,var(--text-tertiary))] shrink-0 transition-transform group-hover:translate-x-0.5" />
    </button>
  );
}

// One compact "at a glance" evidence card. `value` is the large, scannable
// number/status; `pending` renders an honest "—" instead of a fabricated
// zero when the underlying check hasn't run yet.
function GlanceCard({ label, value, caption, pending, testId }) {
  return (
    <div className="rounded-[var(--martian-radius,var(--radius-card))] border border-[var(--martian-rule,var(--border))] bg-[var(--martian-surface,var(--canvas-secondary))] p-3" data-testid={testId}>
      <p className="text-[9px] font-mono uppercase tracking-wide text-[var(--martian-ink-muted,var(--text-tertiary))] mb-1.5">{label}</p>
      <p className={`font-display text-[19px] font-black leading-none mb-1 ${pending ? 'text-[var(--martian-ink-muted,var(--text-tertiary))]' : 'text-[var(--martian-ink,var(--text-primary))]'}`}>
        {pending ? '—' : value}
      </p>
      <p className="text-[10px] text-[var(--martian-ink-muted,var(--text-tertiary))] leading-snug">{caption}</p>
    </div>
  );
}

// Real evidence-grounded opportunity copy, mapped from the actual `type`
// values OpportunityEngine.generate() and DiagnosticEngine.diagnose()
// return (services.py) -- never the free-text fields those services
// don't produce.
const OPPORTUNITY_COPY = {
  visibility_gap: {
    title: 'Close your AI visibility gap',
    why: (o) => `Your brand is mentioned in only ${o.evidence?.current_visibility ?? 0}% of Martian's recorded AI checks.`,
  },
  competitor_gap: {
    title: (o) => `${o.evidence?.competitor || 'A competitor'} is outperforming you in AI answers`,
    why: (o) => `${o.evidence?.competitor || 'A competitor'} appears in ${o.evidence?.competitor_visibility ?? 0}% of checks, vs. your ${o.evidence?.brand_visibility ?? 0}%.`,
  },
  citation_gap: {
    title: 'Close your citation gap',
    why: (o) => `Your brand is cited in only ${o.evidence?.share ?? 0}% of tracked citations.`,
  },
};

const DIAGNOSTIC_COPY = {
  competitor_dominance: { title: 'A competitor is dominating your category', why: 'A competitor is named more often than you in AI answers for this category.', next: 'Analyze their content structure and source coverage to close the gap.' },
  citation_weakness: { title: 'Your brand lacks AI citations', why: "Your brand isn't yet cited as a source behind AI answers, even where others are mentioned.", next: 'Publish answer-first content and structured data to earn citations.' },
  source_authority_weakness: { title: 'Too few sources back your brand', why: 'Few distinct sources currently back up mentions of your brand.', next: 'Build authoritative third-party coverage for your category.' },
  low_recommendation_signal: { title: "You're mentioned, but rarely recommended", why: "You're occasionally mentioned, but rarely the brand being recommended.", next: 'Strengthen content that directly answers recommendation-style queries.' },
};

// Human labels for the real evidence dicts OpportunityEngine/
// DiagnosticEngine attach to their output -- every key here is a real
// field name from services.py, nothing invented for display purposes.
// Milestone 18 (Decision Surface) -- extended with the real evidence keys
// MetaFindingService.detect_for_client() and CommerceFindingService
// produce (meta_intelligence.py / commerce_intelligence.py), verified
// against this milestone's own audit script output, not guessed --
// PreparedWorkCard's new "View evidence" disclosure (below) is the first
// place that generically renders `work.evidence` for every origin, not
// just AI Search's.
const EVIDENCE_LABELS = {
  current_visibility: 'Current visibility', target: 'Target visibility',
  competitor: 'Competitor', competitor_visibility: 'Competitor visibility', brand_visibility: 'Your visibility', gap: 'Visibility gap',
  brand_citations: 'Your citations', total_citations: 'Total citations tracked', share: 'Your citation share',
  leading_competitor: 'Leading competitor',
  brand_source_domains: 'Distinct sources citing you', minimum_recommended: 'Recommended minimum',
  recommendation_rate: 'Recommendation rate',
  executions: 'Checks run so far',
  // Meta Paid Growth (meta_intelligence.py's MetaFindingService).
  campaigns_affected: 'Campaigns affected', campaigns_checked: 'Campaigns checked',
  campaign_names: 'Campaigns', average_roas: 'Average ROAS',
  // Commerce (commerce_intelligence.py's CommerceFindingService).
  unavailable_count: 'Out of stock', products_checked: 'Products checked', product_names: 'Products',
};

// Milestone 18 -- keys that are either internal linkage (never meant for
// a human to read, e.g. entity ids used only to wire up "Compare with
// latest sync") or already shown elsewhere on this same card (
// format_rationale is already surfaced via the "Format:" line) -- so the
// generic evidence disclosure below omits them rather than dumping a raw
// id or repeating a line already shown.
const EVIDENCE_SKIP_KEYS = new Set(['campaign_entity_ids', 'format_rationale']);

// Milestone 4: real, persisted agent_type values prepared_work.py's
// PreparedWorkService writes (agent_type="ai_search"/"commerce") -- the
// first real proof the shared prepared_work shape (added in Milestone 3
// specifically for this) generalizes across evidence domains. Framed as
// "coworkers", matching the AI CGO team narrative, not "Shopify
// analytics" for the commerce one.
const AGENT_LABELS = {
  ai_search: 'AI Search Agent',
  commerce: 'Commerce Agent',
  social: 'Social Agent',
  // Milestone 8: subtle provenance, not a dominant "Meta dashboard" label
  // -- matches the exact phrasing the milestone specified.
  meta: 'Paid Growth · Meta',
  // Milestone 20 (Paid Growth / Google Ads Observe -> Find): harmless,
  // forward-looking groundwork only -- this milestone deliberately does
  // NOT wire a Google prepare-work job (Section 15: "DO NOT JUMP AHEAD",
  // Observe -> Find only), so no real prepared_work item with
  // agent_type="google" exists yet for this label to ever render against.
  // Added now so Prepare (a later milestone) needs no frontend change
  // here when it ships.
  google: 'Paid Growth · Google',
};

// Milestone 5: real, provider-agnostic format taxonomy
// (connectors/social.py normalizes any provider's own media-type string
// onto exactly these three values -- see NormalizedSocialRecord.format).
const FORMAT_LABELS = { static: 'Static post', carousel: 'Carousel', video: 'Video' };

// Milestone 5.1 (judgment layer): real, specific, deterministic
// groundedness_flags values (prepared_work.py) -- each is a caution the
// human reviewer should check, never a block, never a silent rewrite.
// An item with no flags shows nothing here -- absence is never claimed
// to mean "strategically perfect", only "no deterministic grounding
// issue detected".
const GROUNDEDNESS_FLAG_LABELS = {
  public_copy_contains_raw_internal_metric: "This brief may state one of Martian's internal numbers directly as public copy — check before publishing.",
  unsupported_timeframe_or_benchmark: "This brief states a specific timeframe or benchmark Martian hasn't actually confirmed — check before publishing.",
};

const STATUS_BADGE_LABELS = { prepared: 'Awaiting review', reviewing: 'Reviewing', approved: 'Approved', dismissed: 'Dismissed' };
// Milestone 12 (AI CGO Workspace visual system) -- a small status DOT
// replaces the old filled status pill: colour still carries the same
// real meaning (never decorative), but a compact row now reads as one
// line of text with a dot, not two stacked badge chips.
const STATUS_DOT_COLOR = { prepared: 'var(--text-tertiary)', reviewing: 'var(--accent)', approved: 'var(--success)', dismissed: 'var(--text-disabled)' };

function formatEvidenceValue(key, value) {
  // Milestone 18 -- Meta's campaign_names/Commerce's product_names are
  // real arrays of strings (not the numeric/percentage shape every prior
  // evidence field had) -- joined into one readable line rather than
  // Array.prototype.toString's unreadable comma-run.
  if (Array.isArray(value)) return value.join(', ');
  if (typeof value !== 'number') return String(value);
  // average_roas is a real ratio (meta_intelligence.py), not a percent or
  // plain count -- shown as "0.36x" so it isn't misread as a percentage.
  if (/roas/i.test(key)) return `${value}x`;
  // Every other numeric evidence field is either a percentage or a plain
  // count -- the visibility/rate/share/target/gap naming convention
  // already used throughout services.py's opportunity/diagnostic
  // evidence dicts tells us which.
  const isPercent = /visibility|rate|share|target|gap/i.test(key);
  return isPercent ? `${value}%` : String(value);
}

// Milestone 18 (Decision Surface) -- work.type is a real, already-
// persisted field (prepared_work.py's TYPE_BY_FINDING/prepare_for_finding)
// returned by GET /prepared-work/{clientId} today, but never read by this
// page until now. Labels here are a direct, honest translation of the
// same real distinctions prepared_work.py's own _TYPE_PROMPT_HINT already
// draws (content to publish vs. an internal action vs. something to
// investigate) -- nothing invented, no new backend field.
const WORK_TYPE_LABELS = {
  content_brief: 'a content brief',
  answer_improvement: 'a citation-ready answer',
  research_task: 'a research task',
  commerce_action: 'a store fix',
  paid_growth_action: 'a campaign review',
  social_content_brief: 'a social post',
};

// Every real finding Martian can currently ground in evidence, in priority
// order (real opportunities first, then real diagnostics, then the
// snapshot-derived read) -- capped at 3. Section 3 renders these
// compactly; Section 4 elevates findings[0] to the dominant
// recommendation, including its real evidence dict for "Why?". Never
// invents a 4th item to fill space.
// P2.2 -- discoveredCompanyName is optional and additive (companyContext
// loads separately from `data`/briefingAPI.get, see this function's own
// caller): when present it wins over client.name for the exact same
// reason SahibBriefingPage's own brandLabel now prefers it -- this
// function's "Give AI something to say about {brandName}" text is the
// same kind of founder-facing identity string, just for the pre-Company-
// Context snapshot read rather than the page header.
function rankedFindings(data, discoveredCompanyName) {
  const { opportunities = [], diagnostics = [], snapshot, client } = data;
  const website = client?.website || 'your website';
  const brandName = (typeof discoveredCompanyName === 'string' && discoveredCompanyName.trim())
    || client?.name || website;
  const findings = [];

  opportunities.forEach((o, i) => {
    const copy = OPPORTUNITY_COPY[o.type];
    if (!copy) return;
    findings.push({
      key: `opportunity-${o.type}-${i}`,
      title: typeof copy.title === 'function' ? copy.title(o) : copy.title,
      why: copy.why(o),
      next: o.recommended_action || 'Review this opportunity in your workspace for full detail.',
      evidence: o.evidence || null,
      confidence: null,
      grounded: true,
    });
  });

  diagnostics.forEach((d, i) => {
    if (d.type === 'insufficient_data') return;
    const copy = DIAGNOSTIC_COPY[d.type];
    if (!copy) return;
    findings.push({ key: `diagnostic-${d.type}-${i}`, title: copy.title, why: copy.why, next: copy.next, evidence: d.evidence || null, confidence: d.confidence ?? null, grounded: true });
  });

  if (findings.length === 0 && snapshot) {
    findings.push(
      snapshot.mentioned_by_ai
        ? {
            key: 'snapshot-found',
            title: 'Confirm AI is describing you correctly',
            why: `AI already has a working description of ${brandName} — shown above — built from limited public information, not anything you wrote yourself.`,
            next: 'Compare the description above to how you actually want to be positioned. AI will keep repeating it in future answers until something changes what it can find.',
            evidence: null, confidence: null, grounded: true,
          }
        : {
            key: 'snapshot-not-found',
            title: `Give AI something to say about ${brandName}`,
            why: `AI doesn't yet have enough public information to describe ${website} on its own.`,
            next: "Make sure your homepage clearly states what you do in the first paragraph — this is often why AI can't summarize a brand yet.",
            evidence: null, confidence: null, grounded: true,
          }
    );
  }

  return findings.slice(0, 3);
}

// Martian Centre Workspace UX Refactor -- "What Martian found" was silently
// AI-Search-only: rankedFindings() above only ever reads
// opportunities/diagnostics/snapshot, so a real Commerce/Social/Meta
// finding never appeared here, only in "Prepared for you" once turned
// into prepared_work (this is exactly how Paid Growth went missing from
// the experience before). Fixed WITHOUT a new API call or a second
// finding model: `preparedWork` (already fetched by this page for
// "Prepared for you") already carries every domain's real finding --
// title + agent_type + why_now, written once at prepare_for_finding()
// time. This merges the two REAL sources Martian already has into one
// compact, origin-labeled feed: AI Search's own ranked findings (kept
// exactly as before, with their real evidence dict for "why this
// matters") plus one row per non-ai_search prepared_work item (every
// status -- found is found, regardless of where it sits in the human
// review lifecycle, which "Prepared for you" below still owns).
function crossDomainFindingRows(aiSearchFindings, preparedWork) {
  // Milestone 43 -- `domain` is a real, additive field (never previously
  // carried on a found-row): every AI Search finding here is grounded in
  // OpportunityEngine/DiagnosticEngine/the snapshot, all ai_search-domain
  // by construction, and every cross-domain row's real w.agent_type IS
  // the same domain key sahib_intelligence_bridge.py already uses. Lets
  // askMartianAboutFinding hand Talk to Martian a real domain to open a
  // grounded conversation with, instead of a generic label string alone.
  const rows = aiSearchFindings.map((f) => ({ ...f, origin: AGENT_LABELS.ai_search, domain: 'ai_search' }));
  (preparedWork || [])
    .filter((w) => w.agent_type && w.agent_type !== 'ai_search')
    .forEach((w) => {
      rows.push({
        // Milestone 13 -- `next` was always a real field on the prepared_work
        // item itself (recommended_action); it just wasn't being read here
        // before, so a cross-domain row could never show "Martian recommends"
        // the way an AI Search finding already could. No new data, just
        // finally surfacing a field that already existed.
        key: `prepared-${w.id}`, origin: AGENT_LABELS[w.agent_type] || w.agent_type, domain: w.agent_type,
        title: w.title, why: w.why_now, next: w.recommended_action || null, evidence: null, confidence: null, workId: w.id,
      });
    });
  // Hierarchy, not an unbounded list -- point 8's explicit "do not turn
  // everything into cards" instruction. AI Search's own 3 stay (already
  // capped by rankedFindings); this only bounds how many additional
  // cross-domain rows can pile on.
  return rows.slice(0, 6);
}

// Milestone 42 (AI CGO Workspace: Judgment-First Executive Briefing) --
// "What Martian Found"'s real, per-domain data source is GET
// /brand/{clientId}/domain-findings (briefingAPI.domainFindings), a thin
// wrapper around sahib_intelligence_bridge.build_cross_domain_snapshot()
// -- the SAME real aggregator Talk to Martian already uses. That function
// already calls every domain's own real detect_for_client()/equivalent
// and returns a uniform {status, items: [{title, summary, evidence,
// recommended_action, ...}]} shape per domain -- this section never
// re-derives or duplicates that logic, it only decides how to lay the
// already-computed result out.
//
// Seven real domains, in a fixed display order (never reshuffled by
// which one currently has a finding, so the workspace reads as a
// consistent coverage map, not a shifting list). Meta and Google are
// merged into one "Paid Growth" tile (mergePaidGrowthResult below) --
// the SAME "Paid Growth · Meta"/"Paid Growth · Google" framing
// AGENT_LABELS already uses elsewhere on this page, just presented as
// one tile instead of two, since a client typically has at most one
// real connected paid channel today.
const FOUND_DOMAINS = [
  { key: 'ai_search', label: 'AI Search' },
  { key: 'commerce', label: 'Commerce' },
  { key: 'paid_growth', label: 'Paid Growth' },
  { key: 'social', label: 'Social' },
  { key: 'brand_growth', label: 'Brand Growth' },
  { key: 'creative', label: 'Creative' },
  { key: 'creator', label: 'Creator' },
];

const _NOT_WIRED_RESULT = { status: 'not_wired', items: [] };

// Prefers whichever real channel actually has a finding; falls back to
// whichever one is at least real-but-checked (insufficient_data) over
// one that was never wired at all, so "Paid Growth" reports the more
// informative of the two real states rather than an arbitrary default.
function mergePaidGrowthResult(meta, google) {
  const m = meta || _NOT_WIRED_RESULT;
  const g = google || _NOT_WIRED_RESULT;
  if (m.status === 'findings_present') return m;
  if (g.status === 'findings_present') return g;
  if (m.status !== 'not_wired') return m;
  if (g.status !== 'not_wired') return g;
  return m;
}

// Milestone 42 -- the exact four-way honest-limitation vocabulary this
// milestone's own brief requires (Talk to Martian's established `kind`
// values, reused verbatim via LIMITATION_KIND_LABEL, never a fifth
// generic empty state). A bridge status this section has never actually
// observed (e.g. "clean", which none of these 7 domain readers return
// today) still falls back honestly to "insufficient_data" rather than
// crashing on an unmapped key.
const DOMAIN_STATUS_TO_LIMITATION_KIND = {
  insufficient_data: 'insufficient_data',
  clean: 'insufficient_data',
  not_wired: 'capability_unavailable',
  error: 'data_unavailable',
  not_supported: 'unsupported',
};

function domainLimitationSentence(label, status) {
  if (status === 'not_wired') return `Martian isn't connected to real ${label} data yet.`;
  if (status === 'error') return `Martian couldn't retrieve ${label} data just now.`;
  if (status === 'not_supported') return `${label} reporting in this workspace isn't backed by real data yet.`;
  // insufficient_data / clean / anything else honestly uncertain --
  // matches this milestone's own worked example verbatim for Creative.
  return `Not enough real ${label} data yet to determine a finding.`;
}

// Builds the fixed 7-tile array from the real snapshot GET
// /brand/{clientId}/domain-findings returns. `snapshot` is null while
// still loading (every tile then renders its own quiet loading state).
function buildDomainFindingTiles(snapshot) {
  return FOUND_DOMAINS.map(({ key, label }) => {
    const result = key === 'paid_growth'
      ? mergePaidGrowthResult(snapshot?.meta, snapshot?.google)
      : snapshot?.[key];
    return { key, label, result: result || null };
  });
}

// One compact domain tile -- reuses GlanceCard's own established
// bordered-box treatment (already used above for the AI presence/
// sources/surface glance numbers), never a heavier new card style.
// Distinguishes Finding / Evidence / Recommended next move exactly as
// this milestone requires, reusing the SAME "Evidence & reasoning"/
// "Next move" treatment Talk to Martian's own structured replies already
// use (never a second visual language for the same real concepts).
function DomainFindingTile({ label, result, onAskMartian, loading }) {
  const [showEvidence, setShowEvidence] = useState(false);
  const item = (result?.items || [])[0] || null;
  const evidenceEntries = item?.evidence
    ? Object.entries(item.evidence).filter(([k, v]) => !DOMAIN_EVIDENCE_SKIP_KEYS.has(k) && isDisplayableEvidenceValue(v))
    : [];

  return (
    <div
      className="rounded-[var(--martian-radius,var(--radius-card))] border border-[var(--martian-rule,var(--border))] bg-[var(--martian-surface,var(--canvas-secondary))] p-3.5"
      data-testid="domain-finding-tile"
    >
      <p className="text-[9.5px] font-mono uppercase tracking-wide text-[var(--martian-ink-muted,var(--text-tertiary))] mb-1.5" data-testid="domain-finding-label">{label}</p>

      {loading && (
        <p className="text-[11.5px] text-[var(--martian-ink-muted,var(--text-tertiary))]">Checking…</p>
      )}

      {!loading && item && (
        <>
          <p className="text-[13px] font-semibold text-[var(--martian-ink,var(--text-primary))] leading-snug" data-testid="domain-finding-title">{item.title}</p>
          {item.summary && (
            <p className="text-[11.5px] text-[var(--martian-ink-muted,var(--text-secondary))] leading-relaxed mt-1">{item.summary}</p>
          )}

          {item.recommended_action && (
            <div className="mt-2 pl-2 border-l-2 border-[var(--martian-violet,var(--accent))]" data-testid="domain-finding-next-move">
              <p className="text-[9px] font-mono uppercase tracking-wide text-[var(--martian-violet,var(--accent))]">Next move</p>
              <p className="text-[11px] text-[var(--martian-ink,var(--text-primary))] leading-relaxed">{item.recommended_action}</p>
            </div>
          )}

          {evidenceEntries.length > 0 && (
            <>
              <button
                type="button"
                onClick={() => setShowEvidence((v) => !v)}
                data-testid="domain-finding-evidence-toggle"
                aria-expanded={showEvidence}
                className="flex items-center gap-1 text-[10.5px] font-medium text-[var(--martian-violet,var(--accent))] mt-2"
              >
                Evidence &amp; reasoning
                <CaretDown size={10} className={`transition-transform ${showEvidence ? 'rotate-180' : ''}`} />
              </button>
              {showEvidence && (
                <dl className="mt-1.5 space-y-1" data-testid="domain-finding-evidence">
                  {evidenceEntries.map(([k, v]) => (
                    <div key={k} className="flex items-baseline justify-between gap-2 text-[10px]">
                      <dt className="text-[var(--martian-ink-muted,var(--text-tertiary))]">{DOMAIN_EVIDENCE_LABELS[k] || k}</dt>
                      <dd className="font-mono text-[var(--martian-ink,var(--text-primary))] text-right">{formatDomainEvidenceValue(k, v)}</dd>
                    </div>
                  ))}
                </dl>
              )}
            </>
          )}

          {onAskMartian && (
            <div className="mt-2.5 pt-2 border-t border-[var(--martian-rule,var(--border-light))]">
              <AskMartianButton onClick={() => onAskMartian(item)} />
            </div>
          )}
        </>
      )}

      {!loading && !item && (
        <div data-testid="domain-finding-limitation">
          <p className="text-[9px] font-mono uppercase tracking-wide text-[var(--martian-ink-muted,var(--text-tertiary))]">
            {LIMITATION_KIND_LABEL[DOMAIN_STATUS_TO_LIMITATION_KIND[result?.status]] || LIMITATION_KIND_LABEL.insufficient_data}
          </p>
          <p className="text-[11.5px] text-[var(--martian-ink-muted,var(--text-secondary))] leading-relaxed mt-0.5">
            {domainLimitationSentence(label, result?.status)}
          </p>
        </div>
      )}
    </div>
  );
}

// Plain-language description of one real ChangeDetector event -- no
// chart, per the Milestone 2 design principle to prefer plain language
// first. Every field read here is a real field ChangeDetector.detect()
// returns (services.py); nothing is invented. ChangeDetector computes
// events live on every call and does not persist a timestamp of when a
// change was detected -- window_days ("over the last N days") is the
// most specific timing this page can honestly show; confirmed against
// source this milestone, not assumed.
function describeChange(e) {
  switch (e.type) {
    case 'brand_visibility_change':
      return `Your brand's mention rate ${e.direction === 'increase' ? 'increased' : 'decreased'} from ${e.previous_rate}% to ${e.current_rate}% over the last ${e.window_days} days.`;
    case 'competitor_visibility_surge':
      return `${e.competitor} now appears in ${e.current_rate}% of checks, up from ${e.previous_rate}%.`;
    case 'citation_count_change':
      return `Citations ${e.direction === 'increase' ? 'increased' : 'decreased'} from ${e.previous} to ${e.current} over the last week.`;
    default:
      return null;
  }
}

// Milestone 13 (Re-architect the Brand Home UX) -- the ONE place that
// decides what occupies the primary "NOW" position, so Home stops being
// four independent, equally-weighted sections stacked one after another.
// No new priority model: this is exactly the same real ordering the
// page already trusted before this milestone --
// prepared_work.py's rank_prepared_work() for "is anything genuinely
// open", crossDomainFindingRows' own real cross-domain ordering for
// "what's the top real finding" -- just applied ONCE, up front, instead
// of the old page separately rendering "Needs your decision" (open
// work) AND "What Martian found" (top finding) AND "What's happening"
// (real changes) as three permanently-visible blocks regardless of
// whether any of them was actually the most important thing right now.
//
// Priority, and why: a genuinely open decision always wins (a human is
// waiting on Martian, nothing is more urgent) -> a real finding (it always
// carries a real recommended action, i.e. something to DO, not just an
// observation) -> a real ChangeDetector change (real, but has no
// recommended action of its own -- still shown, just as a signal row
// below, never silently dropped) -> the honest baseline state.
function pickNowStory({ openPreparedWork, foundRows, realChanges }) {
  if (openPreparedWork) return { kind: 'prepared_work', work: openPreparedWork };
  if (foundRows.length > 0) return { kind: 'finding', finding: foundRows[0] };
  if (realChanges.length > 0) return { kind: 'change', change: realChanges[0] };
  return { kind: 'baseline' };
}

// Milestone 1 polling: re-checks GET /brand/briefing/status every few
// seconds while a check is in progress, capped so it never polls
// forever, and calls onSettled exactly once when the check completes
// (or the poll window times out) so the caller can re-fetch the full
// briefing -- never re-fetches the heavier GET /brand/briefing itself
// on every tick.
const POLL_INTERVAL_MS = 6000;
const POLL_MAX_ATTEMPTS = 20; // ~2 minutes

function useBriefingStatusPolling(hasClient, onSettled, clientId) {
  const [checkInProgress, setCheckInProgress] = useState(false);
  const [timedOut, setTimedOut] = useState(false);
  const settledRef = useRef(false);
  // Milestone 42 bugfix -- a real, live bug this milestone's own required
  // live UX validation found (not visible from source/tests alone): the
  // effect below used to list `onSettled` directly in its dependency
  // array, but `onSettled` (SahibBriefingPage's own handleSettled) is a
  // plain function redefined on every parent render, never memoized.
  // Every real settle called `setCheckInProgress`, which re-renders the
  // parent, which recreates `onSettled`, which this effect's own
  // dependency array treated as a real change -- tearing the effect down
  // and restarting `poll()` from scratch, forever. Confirmed live: an
  // unbounded, sub-millisecond-interval flood of GET /brand/briefing/
  // status (and everything handleSettled itself re-fetches) hammering
  // the real backend. A ref always holds the LATEST callback without
  // being a reactive value, so the effect now only restarts for a real
  // identity change (hasClient/clientId) -- its original intent.
  const onSettledRef = useRef(onSettled);
  onSettledRef.current = onSettled;

  useEffect(() => {
    if (!hasClient) return undefined;
    let cancelled = false;
    let attempts = 0;
    settledRef.current = false;
    setTimedOut(false);

    const poll = async () => {
      if (cancelled) return;
      try {
        const res = await briefingAPI.status(clientId);
        if (cancelled) return;
        const inProgress = !!res.data?.check_in_progress;
        setCheckInProgress(inProgress);
        if (!inProgress) {
          if (!settledRef.current) { settledRef.current = true; onSettledRef.current(); }
          return; // stop polling -- settled
        }
      } catch {
        // A transient status-poll failure isn't worth surfacing as an
        // error state -- just stop this cycle; the next interval retries.
      }
      attempts += 1;
      if (attempts >= POLL_MAX_ATTEMPTS) {
        setCheckInProgress(false);
        setTimedOut(true);
        return;
      }
      if (!cancelled) setTimeout(poll, POLL_INTERVAL_MS);
    };

    poll();
    return () => { cancelled = true; };
  }, [hasClient, clientId]);

  return { checkInProgress, timedOut };
}

// Milestone 3 -- second-stage polling: only activates once the first
// SoM check has settled (checkInProgress is false), then re-checks
// GET /prepared-work/{clientId} every few seconds until the chained
// prepare_work job has produced real work, or the poll window times
// out. Never polls before there's anything to find, and stops the
// instant real work appears -- no decorative "Martian is working" filler.
const PREPARED_WORK_POLL_INTERVAL_MS = 6000;
const PREPARED_WORK_POLL_MAX_ATTEMPTS = 20; // ~2 minutes

function usePreparedWorkPolling(clientId, active, onReady) {
  const settledRef = useRef(false);
  // Milestone 42 bugfix -- the SAME real bug class this milestone's live
  // validation found in useBriefingStatusPolling above (see that hook's
  // own comment for the full trace): `onReady` (handlePreparedWorkReady)
  // is a plain, non-memoized function, so depending on it directly could
  // restart this poll from scratch on every parent re-render for any
  // client that hasn't yet produced real prepared work (the one real
  // condition under which `active` stays true across renders). Same
  // fix: a ref holds the latest callback without being a reactive
  // dependency.
  const onReadyRef = useRef(onReady);
  onReadyRef.current = onReady;

  useEffect(() => {
    if (!active || !clientId) return undefined;
    let cancelled = false;
    let attempts = 0;
    settledRef.current = false;

    const poll = async () => {
      if (cancelled) return;
      try {
        const res = await preparedWorkAPI.list(clientId);
        if (cancelled) return;
        const items = res.data?.items || [];
        if (items.length > 0) {
          if (!settledRef.current) { settledRef.current = true; onReadyRef.current(items); }
          return; // stop polling -- real prepared work found
        }
      } catch {
        // A transient poll failure isn't worth surfacing -- the next
        // interval retries.
      }
      attempts += 1;
      if (attempts >= PREPARED_WORK_POLL_MAX_ATTEMPTS) return; // give up quietly, section keeps its fallback rendering
      if (!cancelled) setTimeout(poll, PREPARED_WORK_POLL_INTERVAL_MS);
    };

    poll();
    return () => { cancelled = true; };
  }, [clientId, active]);
}

// Section — "Agents at work." Real activity only; an empty list before
// the first check completes renders an honest waiting state, never a
// decorative "thinking" animation. Kept small/quiet, at the very bottom.
// Milestone 13 -- a real relative-time label built ONLY from the
// activity record's own real `created_at` (server.py's
// db.agent_activity.find(...).sort("created_at", -1) -- a genuine
// timestamp every record already carries, just never surfaced before).
// Returns null (never a fabricated "just now") when the field is
// missing or unparseable, so the caller can honestly render nothing.
function relativeTime(iso) {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;
  const diffMin = Math.round((Date.now() - then) / 60000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.round(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.round(diffHr / 24);
  return `${diffDay}d ago`;
}

// Milestone 17 (Experience Model Rebuild), point 10 -- a real audit of
// this exact endpoint found legacy "auditor"/"strategist" scheduled-run
// activity (server.py's pre-Martian agent system, "The Auditor"/"The
// Strategist") writing into the SAME db.agent_activity collection
// GET /agents/activity/recent reads, with no agent-type filter at the
// API level (query is only { user_id, client_id }). Confirmed live: a
// real recorded session showed "Autonomous scheduled auditor run for
// Remote CMO" in Recent. Per file scope (no backend changes this
// milestone) and the brief's own instruction ("remove it from this
// surface" -- a presentation decision, not a data-model one), this is
// fixed here: a real, already-returned field (a.agent_type) is used to
// exclude the two known-legacy identities before Martian Home ever
// renders them. Documented, not silently worked around -- the backend
// gap itself (no agent-type allowlist on this route) is a genuine,
// separately-scoped fix, not touched here.
const LEGACY_AGENT_TYPES = ['auditor', 'strategist'];

function AgentsAtWork({ refreshKey, clientId }) {
  const [activities, setActivities] = useState(null); // null = loading

  useEffect(() => {
    let cancelled = false;
    // Milestone 17 -- fetches more than the 3 actually shown (limit: 8,
    // the API's own real records, nothing invented) specifically so
    // filtering out legacy auditor/strategist entries client-side never
    // silently pushes a real Martian activity item out of view just
    // because a legacy one happened to be more recent.
    //
    // Final Design Partner QA -- GET /agents/activity/recent's own query
    // is { user_id, client_id } but client_id is only applied when the
    // caller sends it (server.py's get_agent_activity_recent); this call
    // never did, so any account owning more than one client (a real,
    // reproducible state in this repo's own dev data, not hypothetical)
    // saw every owned client's activity mixed into "Recent" regardless of
    // which workspace was open. Passing the real, already-resolved
    // clientId scopes this the same way every other briefing call already
    // does -- no backend change needed, the param already existed.
    if (!clientId) { setActivities([]); return () => { cancelled = true; }; }
    agentsAPI.recentActivity({ limit: 8, client_id: clientId })
      .then((res) => {
        if (cancelled) return;
        const real = (res.data?.activities || []).filter((a) => !LEGACY_AGENT_TYPES.includes(a.agent_type));
        setActivities(real.slice(0, 3));
      })
      .catch(() => { if (!cancelled) setActivities([]); });
    return () => { cancelled = true; };
  }, [refreshKey, clientId]);

  return (
    // Milestone 16 -- "Recent activity" -> "Recent" (the milestone's own
    // label); the Pulse icon is dropped ("no large icons"), each row's
    // border-left accent is dropped ("no cards, no colorful badges"),
    // and the timestamp moves to a small mono FIRST column (the
    // milestone's own example: "20h   Meta finding prepared") so this
    // reads as quiet history, never competing with TODAY.
    // Milestone 17 -- eyebrow drops to the same muted/11px peripheral
    // treatment as "Also on my radar" / "Ready for you" (see Section()'s
    // own docstring for why).
    <div data-testid="briefing-agents-at-work">
      <p className="text-[11px] font-semibold uppercase tracking-[0.04em] text-[var(--martian-ink-muted,var(--text-secondary))] mb-2">Recent</p>
      {activities === null && (
        <p className="text-[11.5px] text-[var(--martian-ink-muted,var(--text-tertiary))]">Loading recent activity…</p>
      )}
      {activities?.length === 0 && (
        <p className="text-[11.5px] text-[var(--martian-ink-muted,var(--text-tertiary))] leading-relaxed">Martian is preparing its first check for your brand — real activity will appear here once it runs.</p>
      )}
      {activities?.length > 0 && (
        <ul className="space-y-1.5">
          {activities.map((a) => {
            const time = relativeTime(a.created_at);
            return (
              <li key={a.id} className="flex items-baseline gap-3 text-[11.5px] leading-relaxed">
                {time && <span className="shrink-0 w-8 font-mono text-[var(--martian-ink-muted,var(--text-tertiary))]">{time}</span>}
                <span className="text-[var(--martian-ink-muted,var(--text-secondary))]">{a.task}</span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

// Milestone 7A -- the single shared card for a prepared-work item, used
// both for the pinned "Needs your decision" item (dominant=true, always
// fully expanded, no click-to-collapse) and for every row in "Prepared
// for you" (dominant=false, collapsed by default, click to expand). One
// component, one real source of truth for the HITL actions/groundedness
// display/retry affordance -- never two divergent copies of this logic.
function PreparedWorkCard({ work, dominant, expanded, onToggleExpand, onStatusChange, onRetry, workUpdating, retryingBrief, onGenerateHandoff, generatingHandoff, onCompareLineage, comparingLineage, onAskMartian }) {
  const [showHandoff, setShowHandoff] = useState(false);
  // Milestone 19 -- the handoff's own groundedness note, same quiet
  // treatment as the main one (see showFlags below), kept separate since
  // the two flag lists (work.groundedness_flags vs. handoff's own) are
  // independent and can be open/closed independently.
  const [showHandoffFlags, setShowHandoffFlags] = useState(false);
  // Milestone 18 (Decision Surface) -- Level 4's own disclosure, collapsed
  // by default: the real internal-strategy fields (objective/
  // recommended_angle/success_signal -- prepared_work.py's own module
  // docstring calls these "Martian's/the team's own internal-facing
  // strategy fields", as distinct from the public-facing what_to_create/
  // key_points/suggested_headline shown above it) plus the real evidence
  // dict, together as "the reasoning behind this."
  // Milestone 19 (Decision Flow) -- this is now the ONLY disclosure in
  // this area: M18's separate "Review recommendation" toggle (which used
  // to gate Level 2 behind its own click, inside its own tinted box) is
  // gone -- Level 2 now flows automatically, so showBrief/its box no
  // longer exist. showEvidence keeps its name/meaning unchanged.
  const [showEvidence, setShowEvidence] = useState(false);
  // Milestone 19 -- the groundedness warning's own quiet disclosure (see
  // hasFlags below): collapsed by default, a small note rather than a
  // permanently-open colored box.
  const [showFlags, setShowFlags] = useState(false);
  // Milestone 14 -- once a human has already resolved this item
  // (approved/dismissed), re-showing the entire original why_now/
  // recommended_action by default makes them reread something they've
  // already acted on. Collapsed here; the real content is unchanged and
  // still one click away via "View original recommendation" below.
  const [showOriginal, setShowOriginal] = useState(false);
  const showFull = dominant || expanded;
  const isResolved = work.status === 'approved' || work.status === 'dismissed';
  const hasFlags = Array.isArray(work.groundedness_flags) && work.groundedness_flags.length > 0;
  // Milestone 9: real, persisted content once generated -- null/absent
  // means "not generated yet" (never an empty object standing in for
  // "nothing found").
  const handoffContent = work.handoff?.content || null;
  const handoffFailed = work.handoff && !handoffContent; // generated_at set, generation_error real, content null
  const handoffFlags = Array.isArray(work.handoff?.groundedness_flags) ? work.handoff.groundedness_flags : [];

  // Milestone 11 (Paid Growth Current-State Comparison) -- `comparison`
  // is deliberately local, component-only state, never lifted into the
  // shared preparedWork list the way handoff is: the comparison result
  // is a fresh, on-demand computation that is never persisted on the
  // prepared_work document (see meta_intelligence.py's
  // compare_to_historical_lineage docstring), so there is nothing on
  // `work` for it to live on. onCompareLineage/comparingLineage stay
  // lifted (matching onGenerateHandoff's pattern) only for the shared
  // API-call + error-toast handling in the parent.
  const [comparison, setComparison] = useState(null);
  // Milestone 21 (Google Ads Paid Growth: Find -> Prepare -> Review ->
  // Handoff) -- extended from a hardcoded 'meta' check to any real
  // Paid Growth channel prepared_work.py's own get_meta_lineage_for_
  // comparison (and server.py's compare route) actually accepts today.
  // See that method's own docstring for why its name is a legacy
  // artifact kept for minimal footprint, not a functional Meta-only
  // limit anymore.
  const canCompareLineage = (work.agent_type === 'meta' || work.agent_type === 'google')
    && Array.isArray(work.evidence?.campaign_entity_ids) && work.evidence.campaign_entity_ids.length > 0;
  const handleCompareClick = async () => {
    const result = await onCompareLineage(work);
    if (result) setComparison(result);
  };

  return (
    <div
      className={dominant
        // Milestone 16 -- TODAY stops being a bordered card here too: a
        // single top rule (matching the finding/change hero's own
        // treatment), no border-box, no shadow, no padding-box. A
        // non-dominant row (used inside "Prepared for you") stays bare --
        // no border, no background, no radius -- it relies entirely on
        // the parent list's own hairline dividers, matching SignalRow's
        // geometry exactly.
        ? 'border-t-2 border-[var(--martian-rule-strong,var(--text-primary))] pt-4'
        : 'py-3'}
      data-testid={dominant ? 'prepared-work-dominant-card' : 'prepared-work-row'}
    >
      <button
        type="button"
        onClick={dominant ? undefined : onToggleExpand}
        className={`flex items-start justify-between gap-3 w-full text-left ${dominant ? 'cursor-default' : ''}`}
        data-testid={dominant ? undefined : 'prepared-work-row-toggle'}
      >
        <div className="flex-1 min-w-0">
          {/* Milestone 12 -- one compact status line (dot + plain text),
               not two stacked pill badges. Colour on the dot still
               carries the same real status meaning as the old badge
               did; nothing here is decorative. */}
          <div className="flex items-center gap-1.5 flex-wrap mb-1 text-[10.5px]">
            
            <span className="w-1.5 h-1.5 shrink-0" style={{ background: STATUS_DOT_COLOR[work.status] || 'var(--martian-ink-muted, var(--text-tertiary))' }} aria-hidden="true" />
            {AGENT_LABELS[work.agent_type] && (
              <span className="font-mono uppercase tracking-wide text-[var(--martian-ink-muted,var(--text-tertiary))]" data-testid="prepared-work-agent-label">
                {AGENT_LABELS[work.agent_type]}
              </span>
            )}
            {!dominant && (
              <>
                <span className="text-[var(--martian-ink-muted,var(--text-tertiary))]">·</span>
                <span className="font-medium text-[var(--martian-ink-muted,var(--text-secondary))]" data-testid="prepared-work-status-badge">
                  {STATUS_BADGE_LABELS[work.status] || work.status}
                </span>
              </>
            )}
            {hasFlags && <WarningCircle size={11} weight="fill" className="text-[var(--martian-yellow-ink,#E8B84B)] shrink-0" aria-label="Martian flagged something to check" data-testid="prepared-work-row-flag-icon" />}
          </div>
          <p
            className={dominant ? 'font-display text-[26px] sm:text-[30px] font-bold text-[var(--martian-ink,var(--text-primary))] text-balance leading-[1.15]' : 'text-[13px] font-semibold text-[var(--martian-ink,var(--text-primary))] leading-snug'}
            data-testid="prepared-work-title"
          >
            {work.title}
          </p>
        </div>
        {!dominant && <CaretDown size={13} className={`shrink-0 mt-1 text-[var(--martian-ink-muted,var(--text-tertiary))] transition-transform ${expanded ? 'rotate-180' : ''}`} />}
      </button>

      {showFull && (
        <div className={dominant ? 'mt-2.5' : 'mt-3 pt-3 border-t border-[var(--martian-rule,var(--border-light))]'}>
          {onAskMartian && (
            <div className="mb-2">
              <AskMartianButton onClick={() => onAskMartian(work)} />
            </div>
          )}
          {/* Milestone 14 -- "Why now:"/"Recommended action:" were the
               internal field names read straight off the object, not
               Martian's own judgment voice. Relabeled to match the exact
               "Observation -> Why it matters -> Martian recommends"
               translation the milestone specifies (the finding-kind NOW
               hero already used this language since Milestone 13; this
               brings the prepared-work-kind hero and every expanded
               "Prepared for you" row to the same real copy). Once a
               human has already resolved this item, the same real
               content moves behind "View original recommendation"
               instead of always re-showing it. */}
          {!isResolved && dominant && (
            // Milestone 17 (Experience Model Rebuild) -- the dominant
            // TODAY surface stops reading as a two-field form ("Why this
            // matters:" / "Martian recommends:" as separate labeled
            // blocks). The same two real values (why_now,
            // recommended_action) now flow as two consecutive paragraphs
            // in Martian's own voice -- an observation, then a judgment --
            // matching the finding-kind NOW hero's own narrative
            // treatment below. No label is removed from the COMPACT
            // "Ready for you" rows (see the non-dominant branch below):
            // a scannable list of many items still benefits from a
            // label, only the one dominant story needs to read like a
            // sentence instead of a spec sheet.
            <div className="mb-3 space-y-2.5">
              <p className="text-[15px] text-[var(--martian-ink,var(--text-primary))] leading-relaxed">{work.why_now}</p>
              <p className="text-[15px] text-[var(--martian-ink,var(--text-primary))] leading-relaxed">{work.recommended_action}</p>
              {work.recommended_format && (
                <p className="text-[11.5px] text-[var(--martian-ink-muted,var(--text-tertiary))] leading-relaxed">
                  Format: <span className="text-[var(--martian-ink-muted,var(--text-secondary))] font-medium">{FORMAT_LABELS[work.recommended_format] || work.recommended_format}</span>
                  {work.evidence?.format_rationale ? ` — ${work.evidence.format_rationale}` : ''}
                </p>
              )}
            </div>
          )}

          {!isResolved && !dominant && (
            <>
              {/* Milestone 16 -- a real label line above real sentence-case
                   body copy, never an inline bold-prefixed label
                   competing with the sentence. Kept here (compact
                   "Ready for you" rows only, per Milestone 17) because a
                   scannable operational queue benefits from a label --
                   the dominant TODAY story above reads as prose instead. */}
              <div className="mb-2.5">
                <p className="text-[10.5px] font-medium text-[var(--martian-ink-muted,var(--text-tertiary))] mb-0.5">Why this matters</p>
                <p className="text-[12.5px] text-[var(--martian-ink,var(--text-primary))] leading-relaxed">{work.why_now}</p>
              </div>
              <div className="mb-2">
                <p className="text-[10.5px] font-medium text-[var(--martian-ink-muted,var(--text-tertiary))] mb-0.5">Martian recommends</p>
                <p className="text-[12.5px] text-[var(--martian-ink,var(--text-primary))] leading-relaxed">{work.recommended_action}</p>
              </div>
              {work.recommended_format && (
                <p className="text-[11.5px] text-[var(--martian-ink-muted,var(--text-tertiary))] leading-relaxed mb-2">
                  Format: <span className="text-[var(--martian-ink-muted,var(--text-secondary))] font-medium">{FORMAT_LABELS[work.recommended_format] || work.recommended_format}</span>
                  {work.evidence?.format_rationale ? ` — ${work.evidence.format_rationale}` : ''}
                </p>
              )}
              {!work.recommended_format && <div className="mb-1.5" />}
            </>
          )}

          {isResolved && (
            <div className="mb-2">
              <button
                onClick={() => setShowOriginal((v) => !v)}
                data-testid="prepared-work-view-original"
                className="flex items-center gap-1 text-[11.5px] font-medium text-[var(--martian-violet,var(--accent))]"
              >
                {showOriginal ? 'Hide' : 'View'} original recommendation
                <CaretDown size={11} className={`transition-transform ${showOriginal ? 'rotate-180' : ''}`} />
              </button>
              {showOriginal && (
                <div className="mt-2" data-testid="prepared-work-original-recommendation">
                  <div className="mb-2">
                    <p className="text-[10.5px] font-medium text-[var(--martian-ink-muted,var(--text-tertiary))] mb-0.5">Why this matters</p>
                    <p className="text-[12.5px] text-[var(--martian-ink,var(--text-primary))] leading-relaxed">{work.why_now}</p>
                  </div>
                  <div>
                    <p className="text-[10.5px] font-medium text-[var(--martian-ink-muted,var(--text-tertiary))] mb-0.5">Martian recommends</p>
                    <p className="text-[12.5px] text-[var(--martian-ink,var(--text-primary))] leading-relaxed">{work.recommended_action}</p>
                  </div>
                  {work.recommended_format && (
                    <p className="text-[11.5px] text-[var(--martian-ink-muted,var(--text-tertiary))] leading-relaxed mt-1.5">
                      Format: <span className="text-[var(--martian-ink-muted,var(--text-secondary))] font-medium">{FORMAT_LABELS[work.recommended_format] || work.recommended_format}</span>
                      {work.evidence?.format_rationale ? ` — ${work.evidence.format_rationale}` : ''}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Milestone 19 -- the groundedness mechanism itself is
               untouched (still real, still deterministic, still detected
               exactly as prepared_work.py's own _detect_groundedness_flags
               always has); only the PRESENTATION changes. The old
               permanently-open, bordered yellow box visually said "this
               recommendation is broken" for what is genuinely just "check
               this one thing before publishing." Now a single quiet line
               -- collapsed by default, no border, no fill -- that expands
               to the same real flag text on click. */}
          {hasFlags && (
            <div className="mb-3" data-testid="prepared-work-groundedness-flags">
              <button
                onClick={() => setShowFlags((v) => !v)}
                data-testid="prepared-work-groundedness-toggle"
                className="flex items-center gap-1.5 text-[11.5px] text-[var(--martian-yellow-ink,#7A5F12)]"
              >
                <WarningCircle size={11} />
                Review note · Check before publishing
                <CaretDown size={10} className={`transition-transform ${showFlags ? 'rotate-180' : ''}`} />
              </button>
              {showFlags && (
                <div className="mt-1.5 space-y-1">
                  {work.groundedness_flags.map((flag) => (
                    <p key={flag} className="text-[11.5px] text-[var(--martian-ink-muted,var(--text-secondary))] leading-relaxed" data-testid={`groundedness-flag-${flag}`}>
                      {GROUNDEDNESS_FLAG_LABELS[flag] || `Martian flagged something to check before publishing (${flag}).`}
                    </p>
                  ))}
                </div>
              )}
            </div>
          )}

          {work.prepared_output ? (
            <div className="mb-3">
              {/* Milestone 19 (Decision Flow / Final UX Pass) -- Level 2
                   ("what Martian prepared") is no longer behind its own
                   click. M18 still gated it behind a "Review
                   recommendation" toggle wrapped in a tinted, rounded
                   box -- functionally a document viewer the user had to
                   open, and a container the M19 brief specifically calls
                   out ("white rectangles inside the warm surface"). The
                   real public-facing content (what_to_create/key_points/
                   suggested_headline -- prepared_work.py's own docstring
                   calls these "PUBLIC-FACING copy") now flows directly
                   after the Level 1 judgment paragraphs above, exactly
                   like the brief's own worked example ("I've prepared a
                   campaign review... 4 things I'd check first..."), no
                   box, no click, no label -- it reads as the next
                   sentence of Martian's own narrative, not a revealed
                   document. work.type (unchanged from M18) still frames
                   it so the same three fields read differently for a
                   Meta campaign review vs. an AI Search content brief,
                   with no per-origin branch. Only the internal-strategy
                   fields (objective/recommended_angle/success_signal) and
                   the real evidence dict -- Level 4, "why Martian reached
                   that judgment" -- remain behind one single disclosure
                   below ("Evidence & reasoning"), never competing with
                   the decision. */}
              <p className="text-[14px] text-[var(--martian-ink,var(--text-primary))] leading-relaxed">
                {WORK_TYPE_LABELS[work.type] ? `I've prepared ${WORK_TYPE_LABELS[work.type]} for this. ` : ''}
                {work.prepared_output.what_to_create}
              </p>
              {work.prepared_output.key_points?.length > 0 && (
                <ul className="list-disc pl-4 space-y-1 mt-2">
                  {work.prepared_output.key_points.map((kp, i) => (
                    <li key={i} className="text-[14px] text-[var(--martian-ink,var(--text-primary))] leading-relaxed">{kp}</li>
                  ))}
                </ul>
              )}
              {work.prepared_output.suggested_headline && (
                <p className="text-[14px] text-[var(--martian-ink,var(--text-primary))] leading-relaxed italic mt-2">“{work.prepared_output.suggested_headline}”</p>
              )}

              {/* Level 4 -- the only remaining disclosure in this area
                   (M18 had two nested toggles; this milestone merges them
                   into one, per the explicit "do not add more UI, keep
                   removing" instruction). Same real fields as M18's Level
                   3, same EVIDENCE_LABELS/formatEvidenceValue helpers,
                   generalized across every origin -- nothing deleted,
                   only relabeled to match the brief's own suggested
                   wording and freed from the now-removed outer box. */}
              <button
                onClick={() => setShowEvidence((v) => !v)}
                data-testid="prepared-work-evidence-toggle"
                className="flex items-center gap-1 text-[11.5px] font-medium text-[var(--martian-violet,var(--accent))] mt-3 pt-2 border-t border-[var(--martian-rule,var(--border-light))]"
              >
                Evidence &amp; reasoning
                <CaretDown size={11} className={`transition-transform ${showEvidence ? 'rotate-180' : ''}`} />
              </button>
              {showEvidence && (
                <div className="mt-2 space-y-2" data-testid="prepared-work-evidence">
                  {work.prepared_output.objective && (
                    <div>
                      <p className="text-[10px] font-medium text-[var(--martian-ink-muted,var(--text-tertiary))]">The goal</p>
                      <p className="text-[11.5px] text-[var(--martian-ink-muted,var(--text-secondary))] leading-relaxed">{work.prepared_output.objective}</p>
                    </div>
                  )}
                  {work.prepared_output.recommended_angle && (
                    <div>
                      <p className="text-[10px] font-medium text-[var(--martian-ink-muted,var(--text-tertiary))]">The approach</p>
                      <p className="text-[11.5px] text-[var(--martian-ink-muted,var(--text-secondary))] leading-relaxed">{work.prepared_output.recommended_angle}</p>
                    </div>
                  )}
                  {work.prepared_output.success_signal && (
                    <div>
                      <p className="text-[10px] font-medium text-[var(--martian-ink-muted,var(--text-tertiary))]">What to watch for afterward</p>
                      <p className="text-[11.5px] text-[var(--martian-ink-muted,var(--text-secondary))] leading-relaxed">{work.prepared_output.success_signal}</p>
                    </div>
                  )}
                  {work.evidence && Object.entries(work.evidence).filter(([k, v]) => !EVIDENCE_SKIP_KEYS.has(k) && v != null).length > 0 && (
                    <dl className="grid grid-cols-2 gap-x-4 gap-y-1 pt-1" data-testid="prepared-work-evidence-grid">
                      {Object.entries(work.evidence).filter(([k, v]) => !EVIDENCE_SKIP_KEYS.has(k) && v != null).map(([k, v]) => (
                        <div key={k} className="flex items-baseline justify-between gap-2 text-[10.5px]">
                          <dt className="text-[var(--martian-ink-muted,var(--text-tertiary))]">{EVIDENCE_LABELS[k] || k}</dt>
                          <dd className="font-mono text-[var(--martian-ink,var(--text-primary))]">{formatEvidenceValue(k, v)}</dd>
                        </div>
                      ))}
                    </dl>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="mb-3">
              <p className="text-[12px] text-[var(--martian-ink-muted,var(--text-tertiary))] leading-relaxed mb-2" data-testid="prepared-work-brief-failed">
                Martian captured the reasoning above but couldn't finish drafting the detailed brief — check back shortly.
              </p>
              <button
                onClick={() => onRetry(work)}
                disabled={retryingBrief}
                data-testid="prepared-work-retry-brief"
                className="text-[12px] font-semibold text-[var(--martian-violet,var(--accent))] hover:opacity-80 disabled:opacity-60"
              >
                {retryingBrief ? 'Retrying…' : 'Retry generating this brief'}
              </button>
            </div>
          )}

          {/* Human-only workflow -- Martian never advances this itself;
               every button below is a human decision. */}
          <div className="pt-3 border-t border-[var(--martian-rule,var(--border))] flex items-center justify-between flex-wrap gap-2.5">
            {work.status === 'prepared' && (
              <>
                {/* Milestone 19 -- the dominant story's own status line
                     now IS the decision-framing sentence the brief's
                     mock puts directly beside the buttons ("I've prepared
                     the recommendation for you." -- the same real
                     connective copy M17 introduced, just moved here from
                     a now-removed standalone line above, so it appears
                     exactly once, right where the decision actually is).
                     Compact "Ready for you" rows keep the terser label --
                     a scannable queue of several items benefits from
                     brevity, the one dominant story benefits from voice. */}
                <p className="text-[11px] text-[var(--martian-ink-muted,var(--text-tertiary))]">{dominant ? "I've prepared the recommendation for you." : 'Prepared — awaiting review'}</p>
                <div className="flex gap-2">
                  <button onClick={() => onStatusChange(work, 'dismissed')} disabled={workUpdating} data-testid="prepared-work-dismiss" className="text-[12px] font-medium text-[var(--martian-ink-muted,var(--text-tertiary))] hover:text-[var(--martian-ink-muted,var(--text-secondary))] px-3 py-1.5 disabled:opacity-60">
                    Dismiss
                  </button>
                  <button onClick={() => onStatusChange(work, 'reviewing')} disabled={workUpdating} data-testid="prepared-work-review" className="text-[12px] font-semibold text-white bg-[var(--martian-ink,var(--text-primary))] hover:bg-[var(--martian-ink-muted,var(--text-secondary))] px-3.5 py-1.5 rounded-[var(--martian-radius,var(--radius-button))] disabled:opacity-60 border-2 border-[var(--martian-rule-strong,transparent)] active:translate-y-[1px]">
                    Start reviewing
                  </button>
                </div>
              </>
            )}
            {work.status === 'reviewing' && (
              <>
                {/* Milestone 19 -- same reasoning as the 'prepared' branch
                     above: the dominant story's status line becomes the
                     real decision-framing sentence ("You're reviewing
                     this recommendation."), compact rows keep the terse
                     label. */}
                <p className="text-[11px] text-[var(--martian-ink-muted,var(--text-tertiary))]">{dominant ? "You're reviewing this recommendation." : 'Reviewing'}</p>
                <div className="flex gap-2">
                  <button onClick={() => onStatusChange(work, 'dismissed')} disabled={workUpdating} data-testid="prepared-work-dismiss" className="text-[12px] font-medium text-[var(--martian-ink-muted,var(--text-tertiary))] hover:text-[var(--martian-ink-muted,var(--text-secondary))] px-3 py-1.5 disabled:opacity-60">
                    Dismiss
                  </button>
                  <button onClick={() => onStatusChange(work, 'approved')} disabled={workUpdating} data-testid="prepared-work-approve" className="text-[12px] font-semibold text-white bg-[var(--martian-violet,var(--accent))] hover:opacity-90 px-3.5 py-1.5 rounded-[var(--martian-radius,var(--radius-button))] disabled:opacity-60 border-2 border-[var(--martian-rule-strong,transparent)] active:translate-y-[1px]">
                    Approve
                  </button>
                </div>
              </>
            )}
            {/* Milestone 6 -- the honest "after my decision" state.
                 reviewed_at is the only real timestamp update_status()
                 persists; there is no outcome, re-check, or
                 execution-status field anywhere in prepared_work.py
                 today, so none is shown or implied. */}
            {work.status === 'approved' && (
              <p className="text-[12px] text-[var(--martian-ink-muted,var(--text-secondary))]" data-testid="prepared-work-approved-note">
                Approved{work.reviewed_at ? ` on ${new Date(work.reviewed_at).toLocaleDateString()}` : ''}
                {/* Milestone 14 -- the compact "Approved · Handoff ready"
                     status the brief's section 11 asks for, built from
                     the same real handoffContent/handoffFailed values
                     the handoff UI below already computes -- no new
                     state. */}
                <span className="font-medium text-[var(--martian-ink,var(--text-primary))]"> · {handoffContent ? 'Handoff ready' : handoffFailed ? 'Handoff failed' : 'No handoff yet'}</span>
                <span className="block text-[var(--martian-ink-muted,var(--text-tertiary))] mt-0.5" data-testid="prepared-work-outcome-unknown">
                  Martian hasn't observed or recorded an outcome for this yet.
                </span>
              </p>
            )}

            {/* Milestone 9 (Paid Growth Handoff) -- a second, explicitly
                 human-triggered step, only ever offered once approved.
                 Martian never publishes or executes anything -- this
                 prepares something the brand/agency can act on in their
                 own advertising environment. */}
            {work.status === 'approved' && !work.handoff && (
              <div className="pt-3 mt-1 border-t border-[var(--martian-rule,var(--border-light))]">
                <button
                  onClick={() => onGenerateHandoff(work)}
                  disabled={generatingHandoff}
                  data-testid="prepared-work-generate-handoff"
                  className="text-[12px] font-semibold text-white bg-[var(--martian-violet,var(--accent))] hover:opacity-90 px-3.5 py-1.5 rounded-[var(--martian-radius,var(--radius-button))] disabled:opacity-60 border-2 border-[var(--martian-rule-strong,transparent)] active:translate-y-[1px]"
                >
                  {generatingHandoff ? 'Preparing…' : 'Prepare campaign handoff'}
                </button>
              </div>
            )}

            {work.status === 'approved' && handoffFailed && (
              <div className="pt-3 mt-1 border-t border-[var(--martian-rule,var(--border-light))]" data-testid="prepared-work-handoff-failed">
                <p className="text-[12px] text-[var(--martian-ink-muted,var(--text-tertiary))] leading-relaxed mb-2">
                  Martian couldn't finish preparing the handoff — check back shortly.
                </p>
                <button
                  onClick={() => onGenerateHandoff(work)}
                  disabled={generatingHandoff}
                  data-testid="prepared-work-retry-handoff"
                  className="text-[12px] font-semibold text-[var(--martian-violet,var(--accent))] hover:opacity-80 disabled:opacity-60"
                >
                  {generatingHandoff ? 'Retrying…' : 'Retry preparing the handoff'}
                </button>
              </div>
            )}

            {work.status === 'approved' && handoffContent && (
              <div className="pt-3 mt-1 border-t border-[var(--martian-rule,var(--border-light))]" data-testid="prepared-work-handoff">
                <button
                  onClick={() => setShowHandoff((v) => !v)}
                  data-testid="prepared-work-handoff-toggle"
                  className="flex items-center justify-between gap-1 text-[12.5px] font-semibold text-[var(--martian-ink,var(--text-primary))] w-full text-left"
                >
                  Campaign handoff
                  <CaretDown size={12} className={`transition-transform shrink-0 ${showHandoff ? 'rotate-180' : ''}`} />
                </button>
                {showHandoff && (
                  // Milestone 19 -- same treatment as the main
                  // recommendation: no tinted/rounded box, no ALL-CAPS
                  // mono field-name headers. Plain sentence-case labels
                  // (matching Level 4's own "The goal"/"The approach"
                  // pattern) above real flowing sentences. Every real
                  // field from generate_handoff()'s own schema is still
                  // here -- observed/needs_investigation/
                  // recommended_direction/things_to_check/proposed_actions/
                  // measurement_plan/assumptions_and_open_questions --
                  // nothing removed, only relabeled and unboxed.
                  <div className="mt-3 space-y-2.5">
                    {handoffFlags.length > 0 && (
                      <div>
                        <button
                          onClick={() => setShowHandoffFlags((v) => !v)}
                          data-testid="prepared-work-handoff-groundedness-toggle"
                          className="flex items-center gap-1.5 text-[11.5px] text-[var(--martian-yellow-ink,#7A5F12)]"
                        >
                          <WarningCircle size={11} />
                          Review note · Check before sharing
                          <CaretDown size={10} className={`transition-transform ${showHandoffFlags ? 'rotate-180' : ''}`} />
                        </button>
                        {showHandoffFlags && (
                          <div className="mt-1.5 space-y-1" data-testid="prepared-work-handoff-groundedness-flags">
                            {handoffFlags.map((flag) => (
                              <p key={flag} className="text-[11.5px] text-[var(--martian-ink-muted,var(--text-secondary))] leading-relaxed" data-testid={`handoff-groundedness-flag-${flag}`}>
                                {GROUNDEDNESS_FLAG_LABELS[flag] || `Martian flagged something to check before sharing this (${flag}).`}
                              </p>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                    <div>
                      <p className="text-[10px] font-medium text-[var(--martian-ink-muted,var(--text-tertiary))]">What Martian observed</p>
                      <p className="text-[12.5px] text-[var(--martian-ink,var(--text-primary))] leading-relaxed">{handoffContent.observed}</p>
                    </div>
                    <div>
                      <p className="text-[10px] font-medium text-[var(--martian-ink-muted,var(--text-tertiary))]">What needs investigation</p>
                      <p className="text-[12.5px] text-[var(--martian-ink,var(--text-primary))] leading-relaxed">{handoffContent.needs_investigation}</p>
                    </div>
                    <div>
                      <p className="text-[10px] font-medium text-[var(--martian-ink-muted,var(--text-tertiary))]">Recommended direction</p>
                      <p className="text-[12.5px] text-[var(--martian-ink,var(--text-primary))] leading-relaxed">{handoffContent.recommended_direction}</p>
                    </div>
                    {handoffContent.things_to_check?.length > 0 && (
                      <div>
                        <p className="text-[10px] font-medium text-[var(--martian-ink-muted,var(--text-tertiary))] mb-0.5">Things to check</p>
                        <ul className="list-disc pl-4 space-y-1">
                          {handoffContent.things_to_check.map((t, i) => <li key={i} className="text-[12.5px] text-[var(--martian-ink,var(--text-primary))] leading-relaxed">{t}</li>)}
                        </ul>
                      </div>
                    )}
                    {handoffContent.proposed_actions?.length > 0 && (
                      <div>
                        <p className="text-[10px] font-medium text-[var(--martian-ink-muted,var(--text-tertiary))] mb-0.5">Proposed actions</p>
                        <ul className="list-disc pl-4 space-y-1">
                          {handoffContent.proposed_actions.map((a, i) => <li key={i} className="text-[12.5px] text-[var(--martian-ink,var(--text-primary))] leading-relaxed">{a}</li>)}
                        </ul>
                      </div>
                    )}
                    <div>
                      <p className="text-[10px] font-medium text-[var(--martian-ink-muted,var(--text-tertiary))]">Measurement plan</p>
                      <p className="text-[12.5px] text-[var(--martian-ink,var(--text-primary))] leading-relaxed">{handoffContent.measurement_plan}</p>
                    </div>
                    {handoffContent.assumptions_and_open_questions?.length > 0 && (
                      <div>
                        <p className="text-[10px] font-medium text-[var(--martian-ink-muted,var(--text-tertiary))] mb-0.5">Assumptions &amp; open questions</p>
                        <ul className="list-disc pl-4 space-y-1">
                          {handoffContent.assumptions_and_open_questions.map((q, i) => <li key={i} className="text-[12.5px] text-[var(--martian-ink,var(--text-primary))] leading-relaxed">{q}</li>)}
                        </ul>
                      </div>
                    )}
                    {/* Honest, capability-matched language -- connector
                         sync is manual-only today (verified against
                         connector_sync_route: no recurring/autonomous
                         re-sync exists for any provider). Never implies
                         Martian is continuously watching on its own. */}
                    <p className="text-[10.5px] text-[var(--martian-ink-muted,var(--text-tertiary))] leading-relaxed pt-2 border-t border-[var(--martian-rule,var(--border-light))]" data-testid="prepared-work-handoff-honest-note">
                      This is Martian's prepared handoff — not something Martian has sent or executed. Martian can show what changed when this account is synced again.
                    </p>
                    <button
                      onClick={() => onGenerateHandoff(work)}
                      disabled={generatingHandoff}
                      data-testid="prepared-work-regenerate-handoff"
                      className="text-[11.5px] font-medium text-[var(--martian-violet,var(--accent))] hover:opacity-80 disabled:opacity-60"
                    >
                      {generatingHandoff ? 'Regenerating…' : 'Regenerate handoff'}
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Milestone 11 (Paid Growth Current-State Comparison) -- an
                 explicit, human-triggered action since Martian has no
                 autonomous monitoring: only ever shown for a real Paid
                 Growth item (Meta or, since Milestone 21, Google) with
                 real historical lineage, offered once approved (the
                 lifecycle position the milestone brief suggested;
                 server.py's own route doesn't require "approved" -- see
                 PreparedWorkService.get_meta_lineage_for_comparison's
                 docstring for why). */}
            {work.status === 'approved' && canCompareLineage && !comparison && (
              <div className="pt-3 mt-1 border-t border-[var(--martian-rule,var(--border-light))]">
                <button
                  onClick={handleCompareClick}
                  disabled={comparingLineage}
                  data-testid="prepared-work-compare-lineage"
                  className="text-[12px] font-semibold text-[var(--martian-violet,var(--accent))] hover:opacity-80 disabled:opacity-60"
                >
                  {comparingLineage ? 'Comparing…' : 'Compare with latest sync'}
                </button>
              </div>
            )}

            {work.status === 'approved' && canCompareLineage && comparison && (
              <div className="pt-3 mt-1 border-t border-[var(--martian-rule,var(--border-light))] space-y-3" data-testid="prepared-work-comparison">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <p className="text-[12.5px] font-semibold text-[var(--martian-ink,var(--text-primary))]">
                    Compared with the campaigns identified when this work was prepared
                  </p>
                  <button
                    onClick={handleCompareClick}
                    disabled={comparingLineage}
                    data-testid="prepared-work-recompare-lineage"
                    className="text-[11px] font-medium text-[var(--martian-violet,var(--accent))] hover:opacity-80 disabled:opacity-60 shrink-0"
                  >
                    {comparingLineage ? 'Comparing…' : 'Compare again'}
                  </button>
                </div>

                {/* current_status is returned verbatim from
                     detect_for_client() -- insufficient_data means there
                     is no meaningful synced observation to compare
                     against yet, never rendered as "no change". */}
                {comparison.current_status === 'insufficient_data' ? (
                  <p className="text-[12px] text-[var(--martian-ink-muted,var(--text-tertiary))] leading-relaxed" data-testid="prepared-work-comparison-needs-sync">
                    Martian needs another sync to compare the current finding.
                  </p>
                ) : (
                  <>
                    <p className="text-[10.5px] text-[var(--martian-ink-muted,var(--text-tertiary))] leading-relaxed" data-testid="prepared-work-comparison-honest-note">
                      This comparison reflects the latest available sync.
                    </p>

                    {comparison.still_affected_entity_ids?.length > 0 && (
                      <div data-testid="prepared-work-comparison-still-affected">
                        <p className="text-[9.5px] font-mono uppercase tracking-wide text-[var(--martian-ink-muted,var(--text-tertiary))] mb-1">Still affected</p>
                        <ul className="list-disc pl-4 space-y-0.5">
                          {comparison.still_affected_entity_ids.map((id) => (
                            <li key={id} className="text-[12.5px] text-[var(--martian-ink,var(--text-primary))]">
                              {comparison.campaign_names_by_entity_id?.[id] || 'an unnamed campaign'}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {comparison.no_longer_affected_entity_ids?.length > 0 && (
                      <div data-testid="prepared-work-comparison-no-longer-affected">
                        <p className="text-[9.5px] font-mono uppercase tracking-wide text-[var(--martian-ink-muted,var(--text-tertiary))] mb-1">No longer in the current finding</p>
                        <ul className="list-disc pl-4 space-y-0.5">
                          {comparison.no_longer_affected_entity_ids.map((id) => (
                            <li key={id} className="text-[12.5px] text-[var(--martian-ink,var(--text-primary))]">
                              {comparison.campaign_names_by_entity_id?.[id] || 'an unnamed campaign'}
                            </li>
                          ))}
                        </ul>
                        {/* Required, explicit non-causal clarification --
                             never "improved"/"Martian's recommendation
                             worked"/"ROAS increased"/"recovered". */}
                        <p className="text-[10.5px] text-[var(--martian-ink-muted,var(--text-tertiary))] leading-relaxed mt-1">
                          This only means these campaigns aren't part of the current finding based on the latest available synced observation — it doesn't establish why.
                        </p>
                      </div>
                    )}

                    {comparison.newly_affected_entity_ids?.length > 0 && (
                      <div data-testid="prepared-work-comparison-newly-affected">
                        <p className="text-[9.5px] font-mono uppercase tracking-wide text-[var(--martian-ink-muted,var(--text-tertiary))] mb-1">Newly identified</p>
                        <ul className="list-disc pl-4 space-y-0.5">
                          {comparison.newly_affected_entity_ids.map((id) => (
                            <li key={id} className="text-[12.5px] text-[var(--martian-ink,var(--text-primary))]">
                              {comparison.campaign_names_by_entity_id?.[id] || 'an unnamed campaign'}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {comparison.still_affected_entity_ids?.length === 0
                      && comparison.no_longer_affected_entity_ids?.length === 0
                      && comparison.newly_affected_entity_ids?.length === 0 && (
                      <p className="text-[12px] text-[var(--martian-ink-muted,var(--text-tertiary))]" data-testid="prepared-work-comparison-no-change">
                        No difference between the historical and current finding sets.
                      </p>
                    )}
                  </>
                )}
              </div>
            )}

            {work.status === 'dismissed' && (
              <p className="text-[12px] text-[var(--martian-ink-muted,var(--text-tertiary))]" data-testid="prepared-work-dismissed-note">
                Dismissed{work.reviewed_at ? ` on ${new Date(work.reviewed_at).toLocaleDateString()}` : ''}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Milestone 7A -- workspaceClientId is optional and additive. A brand's
// own login never passes it (every call below falls back to the exact
// same no-arg behavior as before, unchanged); it's only ever set by the
// /workspace/:clientId route, after that route's own explicit POST
// /workspace-context/enter has already verified real ownership.
export default function SahibBriefingPage({ workspaceClientId } = {}) {
  const navigate = useNavigate();
  const { user, activateFullWorkspace } = useAuth();
  const [state, setState] = useState('loading'); // loading | ready | error
  const [data, setData] = useState(null);
  const [sources, setSources] = useState(null); // null = loading, [] = none yet
  const [prompts, setPrompts] = useState(null);
  const [changes, setChanges] = useState(null);
  const [showWhy, setShowWhy] = useState(false);
  const [showEvidence, setShowEvidence] = useState(false);
  const [activating, setActivating] = useState(false);
  const [activityRefreshKey, setActivityRefreshKey] = useState(0);
  const [preparedWork, setPreparedWork] = useState(null); // null = not loaded yet, [] = none yet
  const [groundingNotice, setGroundingNotice] = useState(null); // Phase 0: honest "couldn't prepare confidently" line
  const [workUpdating, setWorkUpdating] = useState(false);
  const [retryingBrief, setRetryingBrief] = useState(false);
  const [generatingHandoff, setGeneratingHandoff] = useState(false);
  const [comparingLineage, setComparingLineage] = useState(false);
  const [socialFacts, setSocialFacts] = useState(null); // null = not loaded yet
  // Milestone 42 -- "What Martian Found"'s real per-domain snapshot. null =
  // not loaded yet (every tile renders its own quiet "Checking…" state,
  // never a fabricated placeholder finding).
  const [domainFindings, setDomainFindings] = useState(null);
  const [companyContext, setCompanyContext] = useState(null); // null = not loaded yet / none set
  // Milestone 7A -- which "Prepared for you" row (if any) is expanded.
  // A single id, not a set: one open row at a time keeps the list calm.
  const [expandedWorkId, setExpandedWorkId] = useState(null);
  // Milestone 12 (Talk to Martian architectural primitive) -- the minimal,
  // explicit context contract the centre hands to the right panel. null
  // means no specific selection (Talk to Martian's own "empty" state).
  // clientId is deliberately always the real WORKSPACE client
  // (client?.id below, resolved from data.client -- the same value
  // already used for every other real API call on this page), never
  // derived from the authenticated user -- this is what keeps identity
  // and context correctly separate inside /workspace/:clientId too (see
  // TalkToMartianPanel's own docstring for the full reasoning).
  const [martianContext, setMartianContext] = useState(null);

  const fetchBriefing = () => {
    briefingAPI.get(workspaceClientId)
      .then((res) => { setData(res.data); setState('ready'); })
      .catch(() => setState('error'));
  };

  const fetchEvidence = (clientId) => {
    if (!clientId) return;
    intelligenceAPI.sources(clientId).then((res) => setSources(res.data || [])).catch(() => setSources([]));
    intelligenceAPI.changes(clientId).then((res) => setChanges(res.data || [])).catch(() => setChanges([]));
    somPromptsAPI.list(clientId).then((res) => setPrompts(res.data?.prompts || [])).catch(() => setPrompts([]));
  };

  const fetchPreparedWork = (clientId) => {
    if (!clientId) return;
    preparedWorkAPI.list(clientId).then((res) => {
      setPreparedWork(res.data?.items || []);
      setGroundingNotice(res.data?.grounding_notice || null);
    }).catch(() => setPreparedWork([]));
  };

  // Milestone 5: real, always-visible social facts -- fetched
  // independently of prepared work, since facts stay visible even when
  // there isn't (yet) enough evidence for a finding.
  const fetchSocialFacts = (clientId) => {
    if (!clientId) return;
    socialFactsAPI.get(clientId).then((res) => setSocialFacts(res.data || null)).catch(() => setSocialFacts(null));
  };

  // Milestone 42 -- a real failure here (unreachable route, transient
  // 500) falls back to an empty object so every tile's own honest
  // "capability_unavailable"-shaped limitation renders, never a crash
  // and never a fabricated finding.
  const fetchDomainFindings = (clientId) => {
    if (!clientId) return;
    briefingAPI.domainFindings(clientId).then((res) => setDomainFindings(res.data || {})).catch(() => setDomainFindings({}));
  };

  // Martian Centre Workspace UX Refactor -- reuses the existing, already-
  // real companyContextAPI.get (Milestone 7A), never called from this
  // page before. A 404/error just means "nothing set yet" -- the chip
  // stays absent, never a fabricated placeholder.
  const fetchCompanyContext = (clientId) => {
    if (!clientId) return;
    companyContextAPI.get(clientId).then((res) => setCompanyContext(res.data || null)).catch(() => setCompanyContext(null));
  };

  useEffect(() => {
    let cancelled = false;
    briefingAPI.get(workspaceClientId)
      .then((res) => {
        if (cancelled) return;
        setData(res.data);
        setState('ready');
        if (res.data?.has_client && res.data?.client?.id) {
          fetchEvidence(res.data.client.id);
          fetchPreparedWork(res.data.client.id);
          fetchSocialFacts(res.data.client.id);
          fetchCompanyContext(res.data.client.id);
          fetchDomainFindings(res.data.client.id);
        }
      })
      .catch(() => { if (!cancelled) setState('error'); });
    return () => { cancelled = true; };
  }, [workspaceClientId]);

  const handleSettled = () => {
    // The first (or a future) SoM check just completed -- re-fetch the
    // full briefing and every real-evidence call once, and let "Agents
    // at work" pick up the new activity entry too. Prepared work itself
    // is picked up separately by usePreparedWorkPolling below, since the
    // chained prepare_work job may still be running at this instant.
    fetchBriefing();
    if (data?.client?.id) {
      fetchEvidence(data.client.id);
      fetchDomainFindings(data.client.id);
    }
    setActivityRefreshKey((k) => k + 1);
  };

  const { checkInProgress, timedOut } = useBriefingStatusPolling(state === 'ready' && data?.has_client, handleSettled, workspaceClientId);

  const handlePreparedWorkReady = (items) => {
    setPreparedWork(items);
    setActivityRefreshKey((k) => k + 1);
  };
  usePreparedWorkPolling(
    data?.client?.id,
    state === 'ready' && !!data?.has_client && !checkInProgress && (!preparedWork || preparedWork.length === 0),
    handlePreparedWorkReady
  );

  const handleWorkStatusChange = async (work, newStatus) => {
    const clientId = data?.client?.id;
    if (!work || !clientId) return;
    setWorkUpdating(true);
    try {
      const res = await preparedWorkAPI.updateStatus(clientId, work.id, newStatus);
      const updated = res.data && res.data.id ? res.data : { ...work, status: newStatus };
      setPreparedWork((prev) => (prev || []).map((w) => (w.id === work.id ? updated : w)));
    } catch {
      toast.error("Couldn't update this right now — please try again");
    } finally {
      setWorkUpdating(false);
    }
  };

  // Real, in-place retry -- never creates a duplicate item (updates the
  // SAME id server-side), only ever offered when the previous attempt
  // genuinely failed (prepared_output is null with a real
  // generation_error), never over a brief that already succeeded.
  const handleRetryBrief = async (work) => {
    const clientId = data?.client?.id;
    if (!work || !clientId) return;
    setRetryingBrief(true);
    try {
      const res = await preparedWorkAPI.retryGeneration(clientId, work.id);
      setPreparedWork((prev) => (prev || []).map((w) => (w.id === work.id ? res.data : w)));
      if (res.data?.prepared_output) toast.success('Brief generated');
      else toast.error("Martian still couldn't generate the brief — please try again shortly");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't retry right now — please try again");
    } finally {
      setRetryingBrief(false);
    }
  };

  // Milestone 9 (Paid Growth Handoff) -- a second, explicitly human-
  // triggered generation, only ever offered once the item is approved
  // (server-enforced too -- see prepared_work.py's generate_handoff).
  // Regeneration overwrites the same handoff field in place, same
  // idempotent pattern as handleRetryBrief above.
  const handleGenerateHandoff = async (work) => {
    const clientId = data?.client?.id;
    if (!work || !clientId) return;
    setGeneratingHandoff(true);
    try {
      const res = await preparedWorkAPI.generateHandoff(clientId, work.id);
      setPreparedWork((prev) => (prev || []).map((w) => (w.id === work.id ? res.data : w)));
      if (res.data?.handoff?.content) toast.success('Handoff prepared');
      else toast.error("Martian couldn't finish preparing the handoff — please try again shortly");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't prepare the handoff right now — please try again");
    } finally {
      setGeneratingHandoff(false);
    }
  };

  // Milestone 11 (Paid Growth Current-State Comparison) -- unlike
  // handleGenerateHandoff, this never writes to `preparedWork` state:
  // the result is a fresh, on-demand computation, deliberately never
  // persisted on the prepared_work document (see
  // meta_intelligence.py's compare_to_historical_lineage docstring), so
  // PreparedWorkCard keeps the returned value in its own local state.
  const handleCompareLineage = async (work) => {
    const clientId = data?.client?.id;
    if (!work || !clientId) return null;
    setComparingLineage(true);
    try {
      const res = await preparedWorkAPI.compareLineage(clientId, work.id);
      return res.data;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't compare right now — please try again");
      return null;
    } finally {
      setComparingLineage(false);
    }
  };

  const handleActivate = async () => {
    setActivating(true);
    try {
      await activateFullWorkspace();
      toast.success('Full Martian workspace activated');
      navigate('/', { replace: true });
    } catch {
      toast.error('Could not activate your workspace — please try again');
    } finally {
      setActivating(false);
    }
  };

  const snapshot = data?.snapshot;
  const client = data?.client;
  const competitors = (data?.overview?.competitors || []).slice(0, 3);
  const findings = data ? rankedFindings(data, companyContext?.company_name?.value) : [];
  // Milestone 3/5: the real, persisted prepared-work item Martian judges
  // most urgent -- the LIST itself already arrives pre-ranked by the
  // backend's rank_prepared_work() (status-first, then real confidence
  // where available, then recency), so index 0 is already the right
  // item. null until any agent has actually produced one.
  const topWork = (preparedWork && preparedWork.length > 0) ? preparedWork[0] : null;
  // Milestone 7A -- "Needs your decision" shows a real item only when
  // one is genuinely still open (prepared/reviewing). An already
  // approved/dismissed topWork means there is nothing left needing a
  // decision right now, even though real prepared work exists -- that
  // real work still surfaces, just in "Prepared for you" below, not
  // here. No manufactured urgency when nothing is actually open.
  const openPreparedWork = topWork && (topWork.status === 'prepared' || topWork.status === 'reviewing') ? topWork : null;
  const restOfPreparedWork = (preparedWork || []).filter((w) => w.id !== openPreparedWork?.id);
  // P2.2 -- this is the founder's own home screen (H1 title, "Martian is
  // getting to know {brandLabel}...", "AI already has a description of
  // {brandLabel}"), so it's the single most visible surface for the
  // identity P2.1 taught Company Discovery to find. It was reading
  // client.name (the raw domain, e.g. "notion.so") directly -- the exact
  // same discovered-name-over-domain preference already established for
  // SoM (services.py's run_unified()) and the sync-competitors route
  // (_resolve_brand_display_name) applies here too. companyContext is
  // already fetched on this page (see companyContextAPI.get above);
  // falls back to the untouched client.name/website chain when no real
  // name has been discovered yet.
  const discoveredCompanyName = companyContext?.company_name?.value;
  const brandLabel = (typeof discoveredCompanyName === 'string' && discoveredCompanyName.trim())
    || client?.name || client?.website || 'your brand';
  const realChanges = (changes || []).map((e) => ({ ...e, description: describeChange(e) })).filter((e) => e.description);
  // Martian Centre Workspace UX Refactor -- the real cross-domain feed
  // (see crossDomainFindingRows's own docstring for why this replaces
  // findings.map() directly below).
  const foundRows = crossDomainFindingRows(findings, preparedWork);
  // Milestone 42 -- the fixed 7-tile "What Martian Found" array. Built
  // fresh on every render from the real domainFindings snapshot (or null
  // while still loading) -- no local state duplication of server data.
  const domainFindingTiles = buildDomainFindingTiles(domainFindings);
  const companyContextChip = companyContextSummary(companyContext);

  // Milestone 12 -- the one place the centre hands context to Talk to
  // Martian. Two real call shapes: a found-row (entityType: "finding") and
  // a prepared-work item (entityType: "prepared_work", carrying its real
  // workflowState so a future backend knows whether it's still open,
  // approved, etc.). entityLabel is always built from real, already-
  // displayed strings (agent label + title) -- never invented here.
  // Milestone 43 -- `domain` added as one more real, optional, additive
  // field on the SAME existing context shape (never a second format):
  // it lets TalkToMartianPanel offer a real, backend-pattern-matching
  // opening question for the specific domain the user actually clicked,
  // instead of generic suggested text that never matched any real
  // routing gate (see that file's own module-level comment for the full
  // root-cause trace). The backend itself still never reads `domain`
  // for anything but the one pre-existing, cosmetic `entityLabel`
  // fallback interpolation -- it is never trusted as evidence or
  // authorization, exactly like every other field in this object.
  const askMartianAbout = (entry) => {
    setMartianContext({
      clientId: client?.id || null,
      surface: 'martian-home',
      entityType: entry.entityType,
      entityId: entry.entityId,
      entityLabel: entry.entityLabel,
      workflowState: entry.workflowState || null,
      domain: entry.domain || null,
    });
  };
  const askMartianAboutFinding = (f) => askMartianAbout({ entityType: 'finding', entityId: f.key, entityLabel: `${f.origin} — ${f.title}`, domain: f.domain || null });
  const askMartianAboutWork = (w) => askMartianAbout({
    entityType: 'prepared_work', entityId: w.id,
    entityLabel: `${AGENT_LABELS[w.agent_type] || w.agent_type} — ${w.title}`, workflowState: w.status, domain: w.agent_type || null,
  });
  const askMartianAboutChange = (c) => askMartianAbout({ entityType: 'change', entityId: c.type, entityLabel: `What's happening — ${c.description}`, domain: 'ai_search' });
  // Milestone 42 -- "What Martian Found"'s own continue-the-conversation
  // hook, the exact same real context shape askMartianAboutFinding/
  // askMartianAboutWork already build (never a second conversational state
  // model): a bridge item that already has a real prepared_work_id
  // resolves as a prepared_work entity (so "Prepare it."/"Why?" resolve
  // against the SAME real item Prepared for You shows); everything else
  // resolves as a finding, keyed by the bridge's own real source_reference
  // when present, falling back to the domain key itself.
  const askMartianAboutDomainFinding = (label, item) => askMartianAbout({
    entityType: item.prepared_work_id ? 'prepared_work' : 'finding',
    entityId: item.prepared_work_id || item.source_reference || item.domain,
    entityLabel: `${label} — ${item.title}`,
    workflowState: item.prepared_work_id ? 'prepared' : null,
    domain: item.domain || null,
  });

  // Milestone 13 -- the single dominant story, chosen once (see
  // pickNowStory's own docstring for the real priority order this
  // reuses, not invents). Everything real that ISN'T picked here still
  // surfaces -- as a compact signal row below, never silently dropped.
  const nowStory = pickNowStory({ openPreparedWork, foundRows, realChanges });
  // Visual System: GLIM mirrors the REAL story above (Product State Engine
  // -- brand/productStates.js). Nothing here is decorative: the face shows
  // approval when a human decision is open, "recommending" when a real
  // finding leads, "something changed" for a real detected change, and
  // "learning" only for the honest baseline state.
  const briefingGlimState = resolveProductState({
    error: state === 'error',
    loading: state === 'loading',
    discovering: checkInProgress,
    approvalRequired: nowStory.kind === 'prepared_work',
    recommending: nowStory.kind === 'finding',
    changed: nowStory.kind === 'change',
    learning: nowStory.kind === 'baseline',
  });
  // Milestone 14 -- real bug found in live video review: when TODAY's
  // story is a prepared_work item, the exact same item could ALSO still
  // appear as its own Signal row (crossDomainFindingRows adds one row
  // per non-ai_search prepared_work item regardless of status, and
  // never knew which one -- if any -- pickNowStory had already
  // promoted). Filtered here by the real workId match, not a second
  // finding model. A genuinely different AI Search finding that merely
  // motivated this prepared_work item (e.g. the raw "visibility gap"
  // finding behind a derived "turn it into a post" work item) is a
  // separate real object and correctly still shows -- only the literal
  // same entity is excluded.
  const secondaryFindingRows = nowStory.kind === 'finding'
    ? foundRows.slice(1)
    : nowStory.kind === 'prepared_work'
      ? foundRows.filter((f) => f.workId !== nowStory.work.id)
      : foundRows;
  // The chosen finding (if any) already accounts for realChanges in
  // pickNowStory's priority order -- real changes are never hidden
  // just because a finding won the NOW slot; they still show here.
  const signalRows = [
    ...secondaryFindingRows,
    ...realChanges.map((c) => ({ key: `change-${c.type}`, origin: "What's happening", title: c.description, isChange: true, change: c })),
  ];

  // Milestone 6.1 -- the persistent, unmissable context banner. Only
  // rendered when this page was reached via an explicitly-entered
  // workspace (workspaceClientId set), never for a brand's own login.
  const workspaceContext = workspaceClientId ? { name: brandLabel } : null;

  return (
    <DashboardLayout
      workspaceContext={workspaceContext}
      rightPanel={(
        // Design Partner Experience Readiness -- the right rail now shows
        // both existing surfaces this batch asked for: Company Context
        // (compact, real, edit-links-out-to-the-real-page rather than a
        // second editor) stacked above the existing, untouched
        // TalkToMartianPanel. `h-full flex flex-col` here mirrors
        // TalkToMartianPanel's own root className exactly, so its internal
        // h-full/max-h sizing resolves against this wrapper the same way
        // it previously resolved directly against <aside> -- no visual
        // change to the panel itself, just one shrink-to-content sibling
        // above it.
        <div className="h-full max-h-[calc(100vh-3rem)] flex flex-col">
          <div className="shrink-0 px-4 pt-4">
            <CompanyContextRail companyContext={companyContext} onEdit={() => navigate('/company-context')} />
          </div>
          <div className="flex-1 min-h-0">
            <TalkToMartianPanel clientId={data?.client?.id} context={martianContext} onClearContext={() => setMartianContext(null)} />
          </div>
        </div>
      )}
    >
      <div className="max-w-3xl" data-testid="martian-briefing-page">
        {/* Header -- evidence-based, not a standing "monitoring" claim.
             Tightened per the Centre Workspace UX Refactor: one status
             line, no dead vertical spacer, a small real Company Context
             chip (only rendered when the brand has actually set one --
             never a placeholder) instead of repeating the full profile. */}
        <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
          <div className="flex items-center gap-3">
            <GLIMState state={briefingGlimState} size={56} showLabel={false} />
            <div>
              <h1 className="font-display text-[19px] font-bold text-[var(--martian-ink,var(--text-primary))] leading-tight">{brandLabel}</h1>
              <p className="text-[12px] text-[var(--martian-ink-muted,var(--text-secondary))]" data-testid="briefing-status-line">
                {checkInProgress ? `Martian is getting to know ${brandLabel}…` : 'Your Martian workspace'}
              </p>
            </div>
          </div>
          {companyContextChip && (
            <button
              onClick={() => navigate('/company-context')}
              data-testid="briefing-company-context-chip"
              className="text-[11px] text-[var(--martian-ink-muted,var(--text-secondary))] hover:text-[var(--martian-ink,var(--text-primary))] hover:underline underline-offset-2 flex items-center gap-1.5 shrink-0"
            >
              <span>Using your company context</span>
              <span className="font-medium text-[var(--martian-ink,var(--text-primary))]">{companyContextChip}</span>
            </button>
          )}
        </div>
        {timedOut && (
          <p className="text-[11.5px] text-[var(--martian-ink-muted,var(--text-tertiary))] mb-3">This is taking longer than usual — check back shortly, or refresh the page.</p>
        )}

        {state === 'loading' && (
          <div className="border border-[var(--martian-rule,var(--border))] bg-[var(--martian-surface,var(--surface))] p-8 text-center text-[13px] text-[var(--martian-ink-muted,var(--text-tertiary))]" data-testid="briefing-loading">
            Preparing your briefing…
          </div>
        )}

        {state === 'error' && (
          <div className="border border-[var(--martian-rule,var(--border))] bg-[var(--martian-surface,var(--surface))] p-8 text-center text-[13px] text-[var(--martian-ink-muted,var(--text-secondary))]" data-testid="briefing-error">
            We couldn't load your briefing right now — refresh to try again.
          </div>
        )}

        {state === 'ready' && !data?.has_client && (
          <div className="border border-[var(--martian-rule,var(--border))] bg-[var(--martian-surface,var(--surface))] p-8 text-center" data-testid="briefing-no-client">
            <p className="text-[13.5px] text-[var(--martian-ink-muted,var(--text-secondary))] mb-4">Add your brand's website to start your briefing.</p>
            <button onClick={() => navigate('/onboarding')} className="bg-[var(--martian-violet,var(--text-primary))] hover:bg-[var(--martian-violet-hover,var(--text-secondary))] text-white font-semibold text-[13.5px] px-5 py-2.5">
              Add your brand →
            </button>
          </div>
        )}

        {state === 'ready' && data?.has_client && (
          // Milestone 17 -- space-y-6 (was space-y-5) at the TOP level
          // (Martian's take / the peripheral cluster / deep evidence /
          // activate CTA), while the peripheral cluster itself uses a
          // tighter space-y-4 internally -- a real, visible gap
          // asymmetry so whitespace alone signals "one dominant thing,
          // then a quieter periphery" without any new border or box.
          <div className="space-y-6">
            {/* Milestone 13 (Re-architect the Brand Home UX) -- NOW is the
                 one dominant story, chosen once by pickNowStory (see its
                 own docstring for the real, unchanged priority order this
                 reuses). This replaces the old "Needs your decision" +
                 "What's happening" + the top row of "What Martian found" --
                 three permanently-visible, equally-weighted sections that
                 read as a report rather than a single point of view.
                 Nothing real is lost: whatever isn't picked here still
                 surfaces, as a Signal row or in Prepared below. */}
            <div data-testid="section-now">
              {/* Milestone 14 -- "NOW" -> "TODAY" with a one-line
                   framing sentence in Martian's own voice, matching the
                   brief's exact example. This is deliberately Martian
                   speaking with a point of view ("Here's what I'd focus
                   on"), not a neutral section label. */}
              {/* Milestone 15 -- "TODAY" is the one place the visual
                   system asserts authority: a real sans-serif label (not
                   the mono-uppercase treatment used for genuinely
                   technical metadata elsewhere), a violet marker instead
                   of a decorative icon. */}
              {/* Milestone 17 (Experience Model Rebuild) -- "TODAY" ->
                   "Martian's take": section 20's own "AI CGO test" (would
                   this still make sense if you replaced "Martian" with
                   "Analytics"?) fails for "TODAY" -- any dashboard widget
                   can be called Today. "Martian's take" cannot be swapped
                   the same way; it only makes sense coming from a point
                   of view. Same testid (section-now), same violet-marker
                   treatment -- purely the label and framing sentence
                   asserting a voice instead of a neutral time window. */}
              <div className="flex items-center gap-1.5 mb-1">
                <span className="w-2 h-2 shrink-0 bg-[var(--martian-violet,var(--accent))]" aria-hidden="true" />
                <p className="text-[13px] font-semibold uppercase tracking-[0.06em] text-[var(--martian-ink,var(--text-primary))]">Martian's take</p>
              </div>
              <p className="text-[12px] text-[var(--martian-ink-muted,var(--text-secondary))] mb-2.5">Here's what I'd focus on right now.</p>

              {/* Phase 0 (P0.1): when a draft wasn't about this business even
                   after one regeneration, Martian says so -- it never shows
                   the contradictory work instead. */}
              {groundingNotice && nowStory.kind !== 'prepared_work' && (
                <p className="text-[12.5px] text-[var(--martian-ink,var(--text-primary))] leading-relaxed mb-3" data-testid="prepared-work-grounding-notice">
                  {groundingNotice}{' '}
                  <a href="/company-context" className="font-semibold text-[var(--martian-violet,var(--accent))] hover:underline">Review Company Context</a>
                </p>
              )}

              {nowStory.kind === 'prepared_work' && (
                <PreparedWorkCard
                  work={nowStory.work} dominant
                  onStatusChange={handleWorkStatusChange} onRetry={handleRetryBrief}
                  workUpdating={workUpdating} retryingBrief={retryingBrief}
                  onGenerateHandoff={handleGenerateHandoff} generatingHandoff={generatingHandoff}
                  onCompareLineage={handleCompareLineage} comparingLineage={comparingLineage}
                  onAskMartian={askMartianAboutWork}
                />
              )}

              {nowStory.kind === 'finding' && (
                // Milestone 16 (Visual Composition / Editorial UX Rebuild)
                // -- TODAY stops being a bordered card: no border-box, no
                // offset shadow, no padding-box. A single top rule opens
                // the block, a single bottom rule closes it -- the
                // "DOMINANT SURFACE" comes from typographic scale (a real
                // hero headline) and open space, not a container.
                // Milestone 17 (Experience Model Rebuild) -- "Why this
                // matters"/"Martian recommends" as separate labeled fields
                // still read as a form. Same two real values (why, next),
                // now flowing as two consecutive paragraphs in Martian's
                // own voice -- an observation, then a judgment -- per the
                // brief's own "narrative hierarchy inside one workspace,
                // not three separate cards" instruction.
                <div className="border-t-2 border-[var(--martian-rule-strong,var(--text-primary))] pt-4" data-testid="now-finding-card">
                  <div className="flex items-center gap-1.5 mb-2">
                    <span className="w-1.5 h-1.5 shrink-0 bg-[var(--martian-violet,var(--accent))]" aria-hidden="true" />
                    <span className="text-[10px] font-mono uppercase tracking-wide text-[var(--martian-ink-muted,var(--text-tertiary))]" data-testid="found-row-origin">{nowStory.finding.origin}</span>
                  </div>
                  <p className="font-display text-[26px] sm:text-[30px] font-bold text-[var(--martian-ink,var(--text-primary))] text-balance leading-[1.15] mb-3">{nowStory.finding.title}</p>

                  <div className="mb-3 space-y-2.5">
                    <p className="text-[15px] text-[var(--martian-ink,var(--text-primary))] leading-relaxed">{nowStory.finding.why}</p>
                    {nowStory.finding.next && (
                      <p className="text-[15px] text-[var(--martian-ink,var(--text-primary))] leading-relaxed">{nowStory.finding.next}</p>
                    )}
                  </div>

                  {/* Milestone 7A fix, carried forward: "no work exists yet"
                       only ever applies to the real AI Search finding when
                       there's genuinely no prepared work anywhere -- a
                       cross-domain row always already has real prepared
                       work behind it (that's how it got here). */}
                  {nowStory.finding.origin === AGENT_LABELS.ai_search && !topWork && (
                    <p className="text-[12px] text-[var(--martian-ink-muted,var(--text-tertiary))] mb-3">
                      {checkInProgress ? 'Martian is preparing the work for this now…' : 'Martian is preparing the work for this now — it will appear here shortly.'}
                    </p>
                  )}

                  <div className="flex items-center gap-4 flex-wrap pt-2 border-t border-[var(--martian-rule,var(--border-light))]">
                    <AskMartianButton onClick={() => askMartianAboutFinding(nowStory.finding)} />
                    {nowStory.finding.evidence && (
                      <button
                        onClick={() => setShowWhy((v) => !v)}
                        data-testid="briefing-why-toggle"
                        className="flex items-center gap-1 text-[12px] font-medium text-[var(--martian-violet,var(--accent))]"
                      >
                        Evidence
                        <CaretDown size={11} className={`transition-transform ${showWhy ? 'rotate-180' : ''}`} />
                      </button>
                    )}
                  </div>
                  {nowStory.finding.evidence && showWhy && (
                    <dl className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1.5" data-testid="briefing-why-evidence">
                      {Object.entries(nowStory.finding.evidence).map(([k, v]) => (
                        <div key={k} className="flex items-baseline justify-between gap-2 text-[11px]">
                          <dt className="text-[var(--martian-ink-muted,var(--text-tertiary))]">{EVIDENCE_LABELS[k] || k}</dt>
                          <dd className="font-mono text-[var(--martian-ink,var(--text-primary))]">{formatEvidenceValue(k, v)}</dd>
                        </div>
                      ))}
                      {nowStory.finding.confidence != null && (
                        <div className="flex items-baseline justify-between gap-2 text-[11px]">
                          <dt className="text-[var(--martian-ink-muted,var(--text-tertiary))]">Confidence</dt>
                          <dd className="font-mono text-[var(--martian-ink,var(--text-primary))]">{Math.round(nowStory.finding.confidence * 100)}%</dd>
                        </div>
                      )}
                    </dl>
                  )}
                </div>
              )}

              {nowStory.kind === 'change' && (
                <div className="border-t-2 border-[var(--martian-rule-strong,var(--text-primary))] pt-4" data-testid="now-change-card">
                  <div className="flex items-center gap-1.5 mb-2">
                    <TrendUp size={12} className="text-[var(--martian-ink-muted,var(--text-tertiary))]" />
                    <span className="text-[10px] font-mono uppercase tracking-wide text-[var(--martian-ink-muted,var(--text-tertiary))]">What's happening</span>
                  </div>
                  {/* A real ChangeDetector event has no recommended_action of
                       its own -- shown as the observation it genuinely is,
                       never given an invented "Martian recommends" line. */}
                  <p className="font-display text-[26px] sm:text-[30px] font-bold text-[var(--martian-ink,var(--text-primary))] text-balance leading-[1.15] mb-3">{nowStory.change.description}</p>
                  <div className="pt-2 border-t border-[var(--martian-rule,var(--border-light))]">
                    <AskMartianButton onClick={() => askMartianAboutChange(nowStory.change)} />
                  </div>
                </div>
              )}

              {nowStory.kind === 'baseline' && (
                <MartianLearningState
                  brandLabel={brandLabel}
                  companyContextSummaryText={companyContextChip}
                  promptsCount={prompts === null ? null : prompts.length}
                  checkInProgress={checkInProgress}
                  onAskMartian={() => askMartianAbout({ entityType: 'learning_state', entityId: 'growth-os-baseline', entityLabel: `${brandLabel} — why isn't there deeper intelligence yet?`, domain: null })}
                  onReviewCompanyContext={() => navigate('/company-context')}
                />
              )}
            </div>

            {/* Milestone 42 (AI CGO Workspace: Judgment-First Executive
                 Briefing) -- "What Martian Found": a fixed, always-present
                 map of all seven real intelligence domains, distinct from
                 both the one dominant story above and the compact signal
                 rows below. Unlike "Also on my radar" (which only ever
                 lists a domain that already has a real, actionable
                 finding), this section shows EVERY domain every time --
                 including an honest "not enough real data yet" tile for
                 one that currently has nothing, so a domain with genuinely
                 insufficient data is never simply invisible (Section 3's
                 own explicit requirement). Each tile distinguishes Finding
                 from Evidence from Recommended next move using the SAME
                 real "Evidence & reasoning"/"Next move" treatment Talk to
                 Martian's own structured replies already use. */}
            <Section eyebrow="What Martian found" testId="section-domain-findings">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {domainFindingTiles.map((tile) => (
                  <DomainFindingTile
                    key={tile.key}
                    label={tile.label}
                    result={tile.result}
                    loading={domainFindings === null}
                    onAskMartian={(item) => askMartianAboutDomainFinding(tile.label, item)}
                  />
                ))}
              </div>
            </Section>

            {/* Milestone 17 (Experience Model Rebuild) -- Also on my
                 radar / Ready for you / Recent now render as ONE tighter
                 peripheral cluster (space-y-4) instead of three sections
                 spaced exactly as far apart as TODAY is from them
                 (the outer space-y-5/space-y-6 below). Whitespace itself
                 now encodes "TODAY is the workspace; this is peripheral
                 vision", matching the brief's explicit "use whitespace as
                 the primary structure, not boxes/borders" instruction --
                 no new component, no border added, purely a spacing and
                 typography change. */}
            <div className="space-y-4">
              {/* ALSO ON MY RADAR -- every other real finding/change, one
                   scannable line each (Milestone 17: "Signals" -> "Also on
                   my radar", the brief's own section-7 wording -- "other
                   things Martian is currently noticing", not another report
                   chapter). Clicking hands the real item to Talk to Martian;
                   deeper investigation happens in conversation or the
                   relevant surface (AI Visibility, Paid Growth, etc.),
                   never by expanding another paragraph inline here. */}
              {signalRows.length > 0 && (
                <div data-testid="section-signals">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.04em] text-[var(--martian-ink-muted,var(--text-secondary))] mb-1">Also on my radar</p>
                  {/* Milestone 15 -- an open stream, not a boxed card: one
                       hairline rule above and below the list (a single rule,
                       matching section 11's own "SIGNALS / ─── / rows"
                       example), each row separated by its own hairline via
                       divide-y. No outer border, no background, no radius. */}
                  <div className="border-t border-b border-[var(--martian-rule,var(--border-light))] divide-y divide-[var(--martian-rule,var(--border-light))]">
                    {signalRows.map((f) => (
                      <SignalRow
                        key={f.key} testId="signal-row"
                        origin={f.origin} title={f.title}
                        onClick={() => (f.isChange ? askMartianAboutChange(f.change) : askMartianAboutFinding(f))}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Martian Bridge -- entry point into the Growth OS
                   Recommendation -> Action Plan decision flow. A sibling
                   of "Ready for you" below (both are real "needs your
                   decision" surfaces), rendered self-contained: it
                   returns null on its own whenever there's nothing
                   genuinely awaiting a decision, so it never disturbs
                   this page's layout when empty. */}
              <RecommendationsAwaitingCard />

              {/* AF-3.14 Bridge Summary Integration -- the wider, passive
                   Growth OS glance (active opportunities, action-plan
                   readiness/blockers, plus recommendations pending as one
                   more segment of the same line). Reads the real
                   GET /intelligence/{workspace_id}/bridge-summary via
                   bridgeSummaryAPI.get(), keyed off the SAME real
                   data.client.id every other call on this page already
                   uses -- no entity resolution, no second aggregation.
                   Self-contained, like RecommendationsAwaitingCard above:
                   renders null whenever there's nothing meaningful, so it
                   never disturbs this page's layout when empty. */}
              <GrowthOSBridgeSignal workspaceId={data?.client?.id} />

              {/* READY FOR YOU -- the complete, real prepared_work queue
                   (every status), unchanged lifecycle/logic. The item
                   already pinned above as TODAY (if any) isn't duplicated
                   here. Milestone 17: "Work ready" -> "Ready for you", the
                   brief's own section-9 wording -- "Martian has already done
                   work for me," not "here are prepared_work documents." */}
              <Section eyebrow="Ready for you" testId="briefing-prepared-for-you">
                {!preparedWork || preparedWork.length === 0 ? (
                  <p className="text-[12.5px] text-[var(--martian-ink-muted,var(--text-tertiary))] leading-relaxed">
                    {checkInProgress ? 'Martian is still gathering evidence — nothing prepared yet.' : "Nothing prepared yet — Martian will draft real work once it has evidence to act on."}
                  </p>
                ) : restOfPreparedWork.length === 0 ? (
                  <p className="text-[12px] text-[var(--martian-ink-muted,var(--text-tertiary))] italic">The one thing Martian has prepared is shown above, awaiting your decision.</p>
                ) : (
                  <div className="divide-y divide-[var(--martian-rule,var(--border-light))]">
                    {restOfPreparedWork.map((w) => (
                      <PreparedWorkCard
                        key={w.id} work={w}
                        expanded={expandedWorkId === w.id}
                        onToggleExpand={() => setExpandedWorkId((id) => (id === w.id ? null : w.id))}
                        onStatusChange={handleWorkStatusChange} onRetry={handleRetryBrief}
                        workUpdating={workUpdating} retryingBrief={retryingBrief}
                        onGenerateHandoff={handleGenerateHandoff} generatingHandoff={generatingHandoff}
                        onCompareLineage={handleCompareLineage} comparingLineage={comparingLineage}
                        onAskMartian={askMartianAboutWork}
                      />
                    ))}
                  </div>
                )}
              </Section>

              {/* Agents at work -- supporting evidence that real work is
                   happening, deliberately secondary/quiet, part of the
                   same peripheral cluster. */}
              <AgentsAtWork refreshKey={activityRefreshKey} clientId={data?.client?.id} />
            </div>

            {/* Milestone 13 -- the full deep-evidence disclosure (real
                 questions checked, glance numbers, AI description,
                 competitors, sources, connected social channels) moves
                 here, to the very bottom, below Prepared and Activity --
                 same real content, same toggle, same data-testids, just
                 no longer nested inside the primary findings flow. This
                 is investigation depth (a curated slice of what
                 AIDiscoveryCommandCenterPage.js shows in full for paid
                 plans), not the primary story. */}
            <div data-testid="section-deep-evidence">
              <button
                onClick={() => setShowEvidence((v) => !v)}
                data-testid="briefing-evidence-toggle"
                className="flex items-center gap-1 text-[12px] font-medium text-[var(--martian-violet,var(--accent))]"
              >
                {showEvidence ? 'Hide' : 'See'} everything Martian checked
                <CaretDown size={12} className={`transition-transform ${showEvidence ? 'rotate-180' : ''}`} />
              </button>

              {showEvidence && (
                <div className="mt-3 border border-[var(--martian-rule,var(--border))] bg-[var(--martian-surface,var(--surface))] p-5 space-y-4">
                  {/* Checked count + freshness + real questions asked */}
                  <div>
                    <div className="flex items-center gap-4 flex-wrap mb-2">
                      <div>
                        <p className="font-display text-[19px] font-black text-[var(--martian-ink,var(--text-primary))] leading-none">{prompts === null ? '—' : prompts.length}</p>
                        <p className="text-[10px] text-[var(--martian-ink-muted,var(--text-tertiary))] mt-1">real questions checked</p>
                      </div>
                      <div className="h-7 w-px bg-[var(--martian-rule,var(--border))]" />
                      <div>
                        <p className="text-[12.5px] font-semibold text-[var(--martian-ink,var(--text-primary))]">{checkInProgress ? 'Checking now' : 'Checked'}</p>
                        <p className="text-[10px] text-[var(--martian-ink-muted,var(--text-tertiary))] mt-1">freshness</p>
                      </div>
                    </div>
                    {prompts?.length > 0 && (
                      <ul className="space-y-1.5" data-testid="briefing-prompts-list">
                        {prompts.map((p) => (
                          <li key={p.id} className="text-[11.5px] text-[var(--martian-ink-muted,var(--text-secondary))] leading-relaxed rounded-[var(--martian-radius,var(--radius-badge))] bg-[var(--martian-surface,var(--canvas-secondary))] px-2.5 py-1.5">
                            “{p.prompt_text}”
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  {/* Glance numbers */}
                  <div className="grid grid-cols-3 gap-2 pt-3.5 border-t border-[var(--martian-rule,var(--border-light))]" data-testid="briefing-glance">
                    <GlanceCard
                      testId="glance-presence" label="AI presence" pending={!snapshot}
                      value={snapshot?.mentioned_by_ai ? 'FOUND' : 'NOT FOUND'}
                      caption={!snapshot ? "Based on Martian's first discovery check" : (snapshot.mentioned_by_ai ? 'AI can already answer what your brand is' : "AI can't yet answer what your brand is on its own")}
                    />
                    <GlanceCard testId="glance-sources" label="External sources" pending={!snapshot} value={snapshot?.source_domains?.length ?? 0} caption="Real external domains associated with this result" />
                    <GlanceCard testId="glance-surfaces" label="Surface checked" pending={!snapshot} value="AI Search" caption="Social & Creators are next" />
                  </div>

                  {/* AI description snapshot */}
                  <div className="pt-3.5 border-t border-[var(--martian-rule,var(--border-light))]" data-testid="briefing-sees-brand">
                    {snapshot?.mentioned_by_ai ? (
                      <>
                        <p className="text-[10px] font-mono uppercase text-[var(--martian-ink-muted,var(--text-tertiary))] mb-1.5">AI already has a description of {brandLabel}</p>
                        <div className="border-l-2 border-[var(--martian-violet,var(--accent))] pl-3">
                          <p className="text-[13px] text-[var(--martian-ink,var(--text-primary))] leading-relaxed">“{snapshot.answer_snippet}”</p>
                        </div>
                        <p className="text-[10.5px] text-[var(--martian-ink-muted,var(--text-tertiary))] leading-relaxed mt-2">
                          This is the description Martian's first discovery check found AI using — not a permanent or universal AI representation.
                        </p>
                      </>
                    ) : (
                      <>
                        <p className="text-[13px] text-[var(--martian-ink,var(--text-primary))] font-semibold mb-1">
                          {snapshot ? `${brandLabel} doesn't yet appear clearly in AI discovery` : `Martian is still learning about ${brandLabel}`}
                        </p>
                        <p className="text-[12px] text-[var(--martian-ink-muted,var(--text-secondary))] leading-relaxed">
                          {snapshot
                            ? "When AI search is asked what this site is, it doesn't yet come up clearly — an early gap worth closing."
                            : "Martian is running its first AI Search check now — this section fills in automatically, no action needed."}
                        </p>
                      </>
                    )}
                  </div>

                  {/* Competitors */}
                  <div className="pt-3.5 border-t border-[var(--martian-rule,var(--border-light))]" data-testid="briefing-competitors">
                    <p className="text-[10px] font-mono uppercase text-[var(--martian-ink-muted,var(--text-tertiary))] mb-1.5">Where your brand is showing up</p>
                    {competitors.length === 0 ? (
                      <p className="text-[12px] text-[var(--martian-ink-muted,var(--text-tertiary))] leading-relaxed">
                        {checkInProgress ? 'Martian is still checking who else appears in these answers.' : "Martian hasn't detected a clear competitor pattern yet."}
                      </p>
                    ) : (
                      <ul className="space-y-1.5">
                        {competitors.map((c) => (
                          <li key={c.name} className="flex items-center justify-between text-[12.5px]" data-testid="briefing-competitor-row">
                            <span className="text-[var(--martian-ink,var(--text-primary))] font-medium">{c.name}</span>
                            <span className="text-[var(--martian-ink-muted,var(--text-secondary))]">mentioned in {c.visibility_rate}% of checks</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  {/* Sources */}
                  <div className="pt-3.5 border-t border-[var(--martian-rule,var(--border-light))]" data-testid="briefing-sources">
                    <p className="text-[10px] font-mono uppercase text-[var(--martian-ink-muted,var(--text-tertiary))] mb-1.5">Sources influencing these answers</p>
                    {sources === null ? (
                      <p className="text-[12px] text-[var(--martian-ink-muted,var(--text-tertiary))]">Loading source evidence…</p>
                    ) : sources.length === 0 ? (
                      <p className="text-[12px] text-[var(--martian-ink-muted,var(--text-tertiary))] leading-relaxed">
                        {checkInProgress ? 'Martian is still gathering source evidence.' : 'No outside sources were cited in the answers Martian has checked so far.'}
                      </p>
                    ) : (
                      <ul className="space-y-1.5" data-testid="briefing-source-list">
                        {sources.slice(0, 5).map((s) => (
                          <li key={s.domain} className="flex items-center justify-between text-[12px]">
                            <span className="font-mono text-[var(--martian-ink,var(--text-primary))]">{s.domain}</span>
                            <span className="text-[var(--martian-ink-muted,var(--text-tertiary))]">cited {s.appearances}× {s.brand_citations > 0 ? `(${s.brand_citations} alongside your brand)` : ''}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  {/* Connected social channels -- real facts only
                       (Milestone 5, social_intelligence.py's
                       get_facts_for_client). Deliberately no judgment
                       language -- every number is descriptive. */}
                  {socialFacts?.channels_connected > 0 && (
                    <div className="pt-3.5 border-t border-[var(--martian-rule,var(--border-light))]" data-testid="briefing-social-facts">
                      <p className="text-[10px] font-mono uppercase text-[var(--martian-ink-muted,var(--text-tertiary))] mb-1.5">Your connected social channels</p>
                      <ul className="space-y-2.5">
                        {socialFacts.channels.map((ch, i) => (
                          <li key={ch.channel_entity_id} className={i > 0 ? 'pt-2.5 border-t border-[var(--martian-rule,var(--border-light))]' : ''} data-testid="social-channel-row">
                            <div className="flex items-center justify-between gap-2 mb-0.5">
                              <span className="text-[12.5px] font-semibold text-[var(--martian-ink,var(--text-primary))]">{ch.handle || ch.platform || 'Connected channel'}</span>
                              {ch.platform && <span className="text-[9.5px] font-mono uppercase text-[var(--martian-ink-muted,var(--text-tertiary))]">{ch.platform}</span>}
                            </div>
                            <p className="text-[11.5px] text-[var(--martian-ink-muted,var(--text-secondary))] leading-relaxed">
                              {ch.posts_synced > 0
                                ? `${ch.posts_synced} recent post${ch.posts_synced !== 1 ? 's' : ''} synced${ch.most_recent_post_days_ago != null ? ` — most recent ${ch.most_recent_post_days_ago === 0 ? 'today' : `${ch.most_recent_post_days_ago} day${ch.most_recent_post_days_ago !== 1 ? 's' : ''} ago`}` : ''}.`
                                : 'No posts synced yet for this channel.'}
                            </p>
                            {ch.avg_engagement_recent != null && (
                              <p className="text-[11.5px] text-[var(--martian-ink-muted,var(--text-secondary))] leading-relaxed mt-0.5">
                                Average engagement across synced posts: <span className="font-mono text-[var(--martian-ink,var(--text-primary))]">{ch.avg_engagement_recent}</span>
                              </p>
                            )}
                            {ch.analytics_capability_active === false && (
                              <p className="text-[10.5px] text-[var(--martian-ink-muted,var(--text-tertiary))] leading-relaxed mt-1" data-testid="social-analytics-capability-note">
                                Martian doesn't have deeper analytics access for this connection yet.
                              </p>
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* CTA to unlock deeper analysis -- Milestone 6: only meaningful
                 pre-activation now that this page is Martian Home at every
                 plan, not just the free-plan teaser. */}
            {user?.plan === 'free' && (
              <div className="border border-[var(--martian-rule,var(--border))] bg-[var(--martian-surface,var(--surface))] p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" data-testid="briefing-activate">
                <div>
                  <p className="text-[13.5px] font-semibold text-[var(--martian-ink,var(--text-primary))]">There's more Martian can discover.</p>
                  <p className="text-[12.5px] text-[var(--martian-ink-muted,var(--text-secondary))] mt-0.5">Activate your full workspace to unlock AI Discovery, Growth, and every connected surface.</p>
                </div>
                <button onClick={handleActivate} disabled={activating} data-testid="briefing-activate-button"
                  className="bg-[var(--martian-violet,var(--text-primary))] hover:bg-[var(--martian-violet-hover,var(--text-secondary))] text-white font-semibold text-[13.5px] px-5 py-2.5 whitespace-nowrap flex items-center gap-1.5 justify-center disabled:opacity-60 active:translate-y-[1px]">
                  {activating ? 'Activating…' : 'Activate full workspace'} {!activating && <ArrowRight size={14} />}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
